import { screenCopyEn } from "./en-screens";
import { syncReviewEn } from "./en-sync";
import type { zhHK } from "./zh-HK";

type Messages<T> = { [K in keyof T]: T[K] extends string ? string : Messages<T[K]> };

export const en = {
  automaticBackup: {
    leaveShort: "Back up before leaving",
    leaveHint:
      "Before leaving, select Back up now and wait for confirmation that this device’s changes are backed up. Automatic backups provide additional protection.",
    title: "Backup history",
    unlockNeeded: "Unlock backups on this device",
    errorSync:
      "Another device has a more recent data version. Let this device finish syncing before backing up or restoring.",
    errorOverdue: "New changes have not been backed up for over 24 hours. Check your network and Drive connection.",
    errorLimit:
      "The full backup exceeds the current 100 MB limit. Download a manual encrypted backup and contact support.",
    close: "Close backup history",
    setup: "Enable automatic backups",
    password: "Recovery password",
    confirmPassword: "Confirm recovery password",
    passwordHint:
      "Use at least 8 characters and keep it separately. If every device and this password are lost, Class in Order cannot decrypt your backups. No daily password entry is needed.",
    trust: "This is a private device. Remember the key and apply the retention rules below",
    retention:
      "Three devices share the latest four successful full backups, with no separate daily versions. Older automatic backups are permanently deleted. Unmerged changes and the restore return point receive extra protection.",
    mismatch: "The recovery passwords do not match.",
    unlock: "Unlock this device",
    unlockHint: "Enter your recovery password once. This private device will remember its key for daily use.",
    enabled: "Automatic backups on",
    disabled: "Automatic backups off",
    disable: "Turn off automatic backups",
    enable: "Turn on automatic backups",
    resume: "Complete first backup",
    now: "Back up now",
    refresh: "Check again",
    checking: "Checking backups",
    working: "Processing backup",
    pending: "Changes awaiting a full backup",
    saved: "This device's changes are backed up",
    unavailable: "Backup pending. The app will retry automatically.",
    lastSuccess: "Last successful backup",
    lastCheck: "Last completed check",
    pendingSince: "Pending since",
    none: "No record yet",
    covered: "Data as of",
    completed: "Completed",
    source: "Source device",
    latest: "Latest",
    recent: "Recent",
    daily: "Daily",
    protected: "Before restore",
    branch: "Protected version",
    inspect: "View",
    restore: "Load",
    details: "Backup contents",
    classes: "Classes",
    records: "Records",
    empty: "Your first completed full backup will appear here.",
    restoreConfirm:
      "Load the backup from {{time}}? The app will first save a return point, then replace this workspace's data. The restored state will sync to connected devices. Different unsynced changes are retained and may need conflict resolution.",
    recoveryConfirm:
      "Load the backup from {{time}} on this new device? The app will first protect local data and then establish a new sync connection. Old device data and cloud history are retained; old devices will need to reconnect.",
    restored: "Data restored. Reopen the workspace to continue.",
    reopen: "Reopen workspace",
    resumeRecovery: "Complete recovery connection",
    drive: "Backup Google Drive",
    connect: "Connect Google Drive",
    openGoogle: "Open Google authorization",
    confirmDrive: "Use this Drive account",
    wrongDrive: "Connect the original Drive account containing your backups.",
    errorPassword: "The recovery password is incorrect, or the recovery package is damaged.",
    errorSetupPassword: "Enter the recovery password from your first setup attempt; retrying does not change it.",
    errorRecentLogin:
      "This device is not yet authorized. Pair it for sync first, or save your work and sign in to the same Class in Order account again before enabling backups.",
    errorDraft: "Finish saving or resolve unsaved changes before loading a backup.",
    errorChanged:
      "Data changed while the safeguard was being created. Existing data was not replaced. Try loading again.",
    errorQuota: "Drive is full. Free up space and retry. Existing backups are retained.",
    errorAuth: "Reconnect the original Google Drive account.",
    errorDevice: "This device needs authorization to access backups.",
    errorFormat: "Backup verification failed. Loading or cleanup has stopped.",
    errorService: "Automatic backup service is not available in this environment yet.",
    errorGeneric: "The operation could not be completed. Please retry. Existing backups are retained.",
    overdue: "New data has been waiting for a full backup for over 24 hours.",
    dismiss: "Dismiss",
    readOnly: "Viewing does not change current data.",
    encrypted: "Encrypted before upload",
    connectionRequired: "Connect to the internet to view or load cloud backups.",
  },
  schoolCalendar: {
    manage: "Calendar settings",
    editMarker: "Edit",
    delete: "Delete",
    eventsList: "Event list",
    title: "School calendar",
    tools: "Markers",
    edit: "Edit calendar",
    save: "Save calendar",
    saved: "Saved",
    editing: "Editing",
    unsaved: "Unsaved",
    saving: "Saving",
    cancel: "Cancel",
    undo: "Undo last change",
    name: "Marker name",
    arrangement: "Lesson arrangement",
    scope: "Applies to",
    schoolWide: "Whole school",
    note: "Notes",
    kinds: {
      publicHoliday: "Public holiday",
      schoolHoliday: "School holiday",
      teacherDevelopment: "Staff development",
      exam: "Exams / paper review",
      activity: "Student activity",
      parentsDay: "Parents' day",
      specialClass: "Special school day",
      specialDay: "Other event",
    },
    short: {
      publicHoliday: "PH",
      schoolHoliday: "H",
      teacherDevelopment: "S",
      exam: "E",
      activity: "A",
      parentsDay: "P",
      specialClass: "T",
      specialDay: "O",
    },
    arrangements: { normal: "Normal lessons", noClasses: "No lessons", special: "Special arrangement" },
    period: "September to August",
    dates: "Year calendar dates",
    chooseDates: "Choose dates",
    chooseEnd: "1 day selected · choose an end date",
    selected: "{{count}} days selected",
    selected_one: "{{count}} day selected",
    selected_other: "{{count}} days selected",
    dateButton: "{{date}}, {{events}}",
    noEvents: "No events",
    zoomMonth: "Expand {{month}}",
    backToYear: "Back to year",
    clear: "Clear selection",
    inspect: "View events",
    apply: "Apply marker",
    update: "Update marker",
    selection: "Selected dates",
    dayItems: "Events on {{date}}",
    close: "Close date events",
    editItem: "Edit {{name}}",
    deleteItem: "Delete {{name}}",
    add: "Add marker",
    official: "Built-in date",
    eventCount: "{{count}} events",
    eventCount_one: "{{count}} event",
    eventCount_other: "{{count}} events",
    conflict: "This event changed in another tab. Cancel editing and try again.",
    saveError: "The calendar could not be saved. Your changes remain on screen.",
    invalid: "Check the calendar event names and dates.",
    archived: "This school year is archived",
    leaveUnsaved: "Discard unsaved calendar changes?",
    range: "{{start}}–{{end}}",
    more: "+{{count}}",
    readOnly: "Read only",
  },
  syncReview: syncReviewEn,
  reportLabels: {
    removedStudent: "Removed student",
    classNumberAndName: "Class number and name",
    answerCount: "Answer count",
    dueDate: "Due date",
    homeworkSubmissionRecords: "Homework submission records",
    exceptionTask: "Exception task",
    currentRoles: "Current roles",
    roleHistory: "Role history",
    school: "School",
    assessment: "Assessment",
    source: "Source",
    quizSessions: "Quiz sessions",
    createdAt: "Created at",
    quiz: "Quiz",
    code: "Code",
    responseScores: "Response scores",
    submittedAt: "Submitted at",
    noSavedRecords: "No saved records",
    thisBrowserDoesNotSupportThePDFCanvas: "This browser does not support the PDF canvas",
  },
  interfaceExtras: {
    homework: "Homework",
    assessment: "Test",
    dictation: "Dictation",
    revision: "Revision",
    materials: "Materials",
    replySlip: "Reply slip",
    exercise: "Exercise",
    worksheet: "Worksheet",
    notes: "Notes",
    classwork: "Classwork",
    revisionDictation: "Revision / dictation",
    other: "Other",
    bug: "Feature problem",
    idea: "Feature suggestion",
    usability: "Usability",
    attendanceSummary: "{{absent}} absent · {{late}} late",
    studentCount: "{{count}} students",
    studentCount_one: "{{count}} student",
    studentCount_other: "{{count}} students",
    missingLabel: "Missing",
    missingToggle: "Missing; select to cancel",
    completeToggle: "Submitted; select to mark missing",
    groupCount: "{{count}} groups",
    groupCount_one: "{{count}} group",
    groupCount_other: "{{count}} groups",
    groupNumber: "Group {{number}}",
    or: " or ",
    subject: "Subject",
    duty: "Role",
    calendarFile: "Calendar file",
    genderSuffix: ", {{gender}}",
    widen: "Widen",
    cancelWiden: "Remove widening",
    publicHolidayDate: ", public holiday",
    noteSuffix: ", notes: {{note}}",
    subjectPrefix: "{{subject}}: ",
    unknownError: "Unknown error",
    attendanceStatus: "Attendance status",
    materialsStatus: "Missing books and notes status",
  },
  screenCopy: screenCopyEn,
  language: {
    choose: "Interface language",
  },
  personalTools: {
    coursework: {
      title: "Coursework to follow up",
      total: "Classes: {{classes}} · Tasks: {{tasks}}",
      tasks: "{{count}} tasks",
      tasks_one: "{{count}} task",
      tasks_other: "{{count}} tasks",
      openClass: "View {{count}} tasks to follow up in {{name}}",
      openClass_one: "View {{count}} task to follow up in {{name}}",
      openClass_other: "View {{count}} tasks to follow up in {{name}}",
    },
    dayLabel: "My agenda",
    globalNavigation: "My work",
    workHome: "My workspace",
    workHomeHint: "Check your agenda or choose a class to begin.",
    agendaEntryHint: "View your schedule and classroom events",
    classContext: "Current class",
    demoMode: "Demo data",
    permanentlyDelete: "Delete permanently",
    blank: "Blank",
    saved: "Autosaved",
    saving: "Saving…",
    cancel: "Cancel",
    close: "Close",
  },
  toolPage: {
    more: "More",
    overflow: "Switch tool tabs",
    closeHelp: "Close help",
    help: {
      coursework: {
        title: "Coursework and grades help",
        first: "Use Coursework overview to see recent assessments and work that needs follow-up.",
        second: "Choose coursework in Record entry to record scores and submission or assessment status.",
        third:
          "Use Grade table to filter and export scores. In Coursework overview, select Compare terms to compare performance across terms.",
      },
      calendar: {
        title: "Calendar help",
        first: "Select '+ New event' or a calendar date to add homework, tests, dictation or reminders.",
        second:
          "Homework, tests and dictation link to Coursework and grades. Use Record entry to enter scores and update status.",
        third: "Filter the school calendar and event types, or select an event for details.",
      },
    },
  },
  schoolYears: {
    backupScope: "Backups include every school year. Restoring replaces local data for all school years.",
    selector: "School year",
    manage: "Manage school years",
    title: "School year management",
    open: "Open",
    archived: "Archived",
    current: "Default school year",
    dateCurrent: "Current school year",
    history: "Previous years",
    viewYear: "View {{name}}",
    returnCurrent: "Back to current year",
    viewing: "Viewing {{name}}",
    preparing: "Preparing {{name}}",
    prepareNext: "Prepare next year",
    continuePreparing: "Continue setup",
    nextYear: "Next school year",
    future: "In preparation",
    editable: "Editable",
    setCurrent: "Set as default",
    archive: "Archive",
    reopen: "Reopen",
    create: "New school year",
    startYear: "Starting year",
    source: "Reuse class settings",
    empty: "Start from scratch",
    createHint: "Only class settings are reused. Rosters, records and student selections are not copied.",
    legacyHint: "Existing data stays intact and has not been reassigned by date.",
    archiveHint: "Archived years remain available to view and export. Reopen a year to make changes.",
    archiveConfirm: "Archive '{{name}}'? Records are retained and become read-only.",
    reopenConfirm: "Reopen '{{name}}' to allow changes to its records?",
    readOnly: "This school year is archived and available for viewing and export only.",
    readOnlyError: "This school year is archived. Reopen it in School year management first.",
    unsaved: "You have unsaved changes. Discard them and switch school year?",
    busy: "Saving is in progress. Wait before switching school year.",
    saved: "School year settings saved",
    delete: "Delete entire school year",
    deleteTitle: "Delete school year data",
    deleteHint:
      "Permanently delete the classes, students and all records in '{{name}}'. Other school years are retained.",
    counts: "{{classes}} classes · {{students}} students · {{records}} records",
    confirmation: "Enter the school year name to confirm",
    backupFirst: "Download an encrypted backup of all school years first",
    backupChecked: "I have saved a restorable backup",
    cancel: "Cancel",
    confirmDelete: "Delete permanently",
    close: "Close school year management",
    failed: "Could not save school year settings. Existing data is retained.",
    duplicate: "This school year already exists.",
    invalid: "Enter a valid starting year.",
    stale: "Data has changed. Review the deletion scope again.",
    chooseYear: "Choose a school year first",
    termSetup: "Reset terms",
    selected: "Viewing",
    switchFailed: "Could not switch school year.",
  },
  dailyMemos: {
    tabs: "Class affairs tabs",
    memos: "Daily memos",
    affairs: "Class affairs log",
    count: "{{count}} students",
    count_one: "{{count}} student",
    count_other: "{{count}} students",
    add: "New memo",
    placeholder: "e.g. Going home independently",
    edit: "Edit student list",
    done: "Done",
    delete: "Delete",
    deleteLabel: "Delete {{title}}",
    deleteConfirm: "Delete '{{title}}' and its selected students?",
    noSelected: "No students selected",
    noStudents: "No students added yet",
    empty: "No daily memos yet",
    selected: "Selected",
    unselected: "Not selected",
    selectStudent: "Select student {{number}}, {{name}}",
    unselectStudent: "Deselect student {{number}}, {{name}}",
    duplicate: "This memo already exists. Use a different name.",
    stale: "This memo has been removed. Select it again.",
    failed: "Could not save the memo. Try again.",
    missingClass: "Class not found.",
  },
  historyImport: {
    title: "Import records",
    method: "Import method",
    methods: {
      excel: "Excel",
      csv: "CSV",
      paste: "Paste table",
    },
    fileLabels: {
      excel: "Excel file (.xlsx, up to 10 MB)",
      csv: "CSV file (.csv, up to 10 MB)",
    },
    encoding: "CSV encoding",
    encodings: {
      utf8: "UTF-8",
      big5: "Traditional Chinese (Big5)",
    },
    paste: "Table content",
    analyze: "Read table",
    pastedSheet: "Pasted table",
    reading: "Reading data…",
    readFailed: "Could not read data.",
    sourceRow: "Source row",
    overwrite: "Allow individual rows to overwrite existing records",
    errors: {
      fileType: "Choose a .csv file.",
      size: "Data must not exceed 10 MB.",
      empty: "No readable data found.",
      quotes: "The table contains incomplete quotation marks. Correct them and try again.",
      columns: "Each table may contain up to 80 columns.",
      rows: "Each table may contain up to 5,000 records. Split it before importing.",
      encoding: "The encoding does not match. Change the CSV encoding and try again.",
    },
  },
  brand: {
    name: "課序",
    english: "Class in Order",
    symbol: "課",
    tagline: "Your classroom and class management workspace",
    transition: "Previously Classroom Operations",
    home: "Class in Order home",
    monthlyPlan: "Class in Order monthly plan",
    backupFilenamePrefix: "Class-in-Order-encrypted-backup",
  },
  landing: {
    title: "Record it in class.<br/><strong>Less catching up afterwards.</strong>",
    description:
      "Tap a seat card when a student answers. Use your iPad or computer to keep records as you teach and reduce work after class.",
    demoAction: "Try Demo without signing in",
    betaAction: "Sign in to Free Trial →",
    supportingLine: "Keep the details of each lesson.",
    skip: "Skip to main content",
    navigation: "Home navigation",
    demoNav: "Try Demo",
    trialNav: "Start free trial",
    trialPreview: "Preview Free Trial",
    entryNote: "No sign-in for Demo · Free Trial data stays on your device · No credit card",
    previewLabel: "Class in Order · Teaching workspace",
    heroAlt: "Class in Order student records preview (fictional demo data)",
    previewCaption: "Actual product screen · All classes and student data are fictional",
    highlightDemo: "Try Demo without signing in",
    highlightLocal: "Free Trial data stays on your device",
    highlightBackup: "Download encrypted backups",
    featuresKicker: "From teaching to follow-up",
    featuresTitle: "Use what you record in class, afterwards.",
    featuresDescription: "Record in the moment, review by student, and use the details for follow-up.",
    recordDemoLabel: "Classroom recording example",
    recordDemoClass: "2A · Class responses",
    recordDemoBadge: "Try it",
    recordDemoInstruction: "Select a student to record an answer",
    recordDemoBlackboard: "Board · Teacher view",
    recordDemoAnswer: "Record an answer for student {{number}}, {{name}}",
    recordDemoNumber: "No. {{number}}",
    recordDemoCount: "{{count}} answers",
    recordDemoCount_one: "{{count}} answer",
    recordDemoCount_other: "{{count}} answers",
    recordDemoTotal: "{{count}} answers today",
    recordDemoTotal_one: "{{count}} answer today",
    recordDemoTotal_other: "{{count}} answers today",
    recordDemoSave: "Save today's records",
    recordDemoReady: "Try a seat card to see the answer count change.",
    recordDemoDirty: "Recorded, but not saved. Now select 'Save today's records'.",
    recordDemoSaved: "Example complete! Try the full classroom workflow in Demo.",
    recordDemoNotice: "Interactive example only. No classroom records are written.",
    recordDemoReset: "Reset example",
    sampleStudent1: "陳幼羚",
    sampleStudent2: "李允行",
    sampleStudent3: "黃允行",
    sampleStudent4: "張英秀",
    sampleStudent5: "林修端",
    sampleStudent6: "何幼羚",
    trustLabel: "Device-local storage",
    trustTitle: "Your student records stay on your device.",
    trustDescription: "Free Trial keeps rosters and classroom records on your device, without uploading them.",
    trustAction: "About storage and backups",
    perspectiveKicker: "A fuller view of each student",
    perspectiveTitle: "See how students participate, beyond their scores.",
    perspectiveDescription:
      "Scores may be in Excel while responses, homework and notes are elsewhere. Class in Order brings them together to help you review student performance.",
    perspectiveDetail:
      "Use one roster to view records and term summaries, without searching across separate spreadsheets.",
    typeGrades: "Grades",
    typeAnswers: "Class responses",
    typeHomework: "Homework submission",
    typeAttendance: "Attendance",
    typeMaterials: "Books and notes preparation",
    typeNotes: "Notes",
    overviewLabel: "Automatic summaries",
    overviewTitle: "Find who needs follow-up without counting each record.",
    overviewDescription:
      "Responses and days with missing books and notes are summarised by student. Filter students who have forgotten books or notes to find cases that need attention.",
    overviewDetail: "View by student, date or record range, and export what you need.",
    overviewAlt: "Student record summaries and filters (fictional demo data)",
    followupLabel: "Individual student follow-up",
    followupTitle: "Have specific records ready for follow-up.",
    followupDescription:
      "Open a student's daily records to check responses, books and notes preparation, and comments. Find dates and details when talking with students or parents.",
    followupDetail: "Move from a class overview to individual details to plan your next step.",
    followupAlt: "Student daily records and notes (fictional demo data)",
    supportKicker: "Fits your existing workflow",
    supportTitle: "Fewer steps to record and organise.",
    transferLabel: "Import and export",
    transferTitle: "Keep using your existing Excel records.",
    transferDescription:
      "Import supported Excel or CSV records. Preview and map columns before confirming, to reduce retyping.",
    transferStep1: "Choose a file",
    transferStep2: "Preview and map columns",
    transferStep3: "Confirm import",
    transferDetail: "Filter by student, date or task, then export to a spreadsheet for reporting.",
    courseworkLabel: "Homework, tests and scores",
    courseworkTitle: "Keep track of coursework.<br/>Enter scores with ease.",
    courseworkDescription:
      "See due dates, missing submissions and marking progress for homework, tests and dictation. Open a record to enter scores and update each student's status.",
    courseworkDetail: "See missing work, late submissions and unmarked work in one place.",
    courseworkAlt: "Test submission status and marking progress (fictional demo data)",
    courseworkEntryAlt: "Student submission status and score fields (fictional demo data)",
    courseworkListStep: "① Review submission status and marking progress",
    courseworkEntryStep: "② Open a record to enter student scores",
    gradesLabel: "Coursework and grades",
    gradesTitle: "Enter scores and see the class picture.",
    gradesDescription:
      "Saved scores are summarised into average percentages and distributions. Review homework, tests or dictation, then look at individual results.",
    gradesDetail: "Move from entering scores to a class overview with less calculation and spreadsheet work.",
    gradesAlt: "Coursework and grades screen (fictional data)",
    seatingLabel: "Seating and class management",
    seatingTitle: "Bring class arrangements into the classroom.",
    seatingDescription:
      "Manage seating, student roles and everyday tasks using your student roster, connecting classroom arrangements with later records.",
    seatingDetail: "One workspace for both subject teaching and homeroom duties.",
    seatingAlt: "Seating and class management screen (fictional data)",
    featureAction: "Try a demo class",
    featureCaption: "Fictional demo class · Actual interface screenshots",
    dataKicker: "Data storage",
    dataTitle: "Student and classroom records stay on your device.",
    dataDescription:
      "Free Trial rosters and classroom records stay in this device's browser. They are not uploaded to the cloud. You decide when to export and back up.",
    localTitle: "Saved on this device",
    localDescription:
      "Rosters and classroom records stay in this device's browser and are not sent to a server. Free Trial still needs a connection to check account access.",
    backupTitle: "Take your backup with you",
    backupDescription:
      "Download encrypted backups manually and keep the password separately. Restoring replaces existing local data and requires confirmation.",
    dataTitleSynced: "Your device stores the data. You choose whether to sync.",
    dataDescriptionSynced:
      "Classroom data are saved in this browser first. You can choose Google Drive sync to continue with encrypted data across your own devices.",
    localDescriptionSynced:
      "Rosters and classroom records are stored in this browser. Enabling sync sends encrypted sync data to the Drive you authorize. Free Trial still checks account access online.",
    syncAvailableTitle: "Enable cross-device sync when you need it.",
    syncAvailableDescription:
      "Free Trial users can choose Google Drive sync and pair other private devices once using a QR code, or set it up later.",
    syncTitle: "Free Trial currently supports one device.",
    syncDescription:
      "Use the same device and browser. Signing in does not sync records across devices. Records do not appear automatically on a new device or browser. Back up before clearing site data.",
    trialKicker: "Start with a demo class",
    trialTitle: "Try a lesson, then decide.",
    trialDescription:
      "Explore Demo first, then sign in to Free Trial to manage your own classes and classroom records.",
    demoTitle: "Explore the demo",
    demoDescription: "No sign-in required. Try fictional classes. Data lasts for this visit and resets on reload.",
    demoFeature1: "Try the workspace directly",
    demoFeature2: "No real data retained",
    demoFeature3: "Reset demo data at any time",
    demoFeature4: "No account access checks required",
    demoEntry: "Open Demo",
    betaTitle: "Free Trial on your device",
    betaIndex: "02 / FREE TRIAL",
    betaDescriptionSynced:
      "Sign in to create your classes and records. Data is saved locally first, with optional Google Drive sync to continue on your own computer and iPad.",
    betaFeature3Synced: "Optional device sync and independent encrypted backups",
    betaDescription:
      "Free Trial currently supports one device. Sign in to use your own classes, with records stored in that device's browser. Install it as a PWA to open from your home screen.",
    betaFeature1:
      "Use in real classes, subject to school policies and data protection requirements. No credit card required.",
    betaFeature3: "Download encrypted backups manually",
    betaFeature4: "Access is checked online at startup and when returning to the app",
    betaDetails: "Connection and read-only arrangements",
    betaAccessNotice:
      "During a network failure, saving remains available for 10 minutes after the last successful check, then becomes read-only. You can still view, export and back up. Expired or revoked access immediately becomes read-only without deleting local data.",
    betaDataNotice:
      "Records do not sync automatically to another browser. Clearing site data may remove them. Download encrypted backups regularly.",
    betaEntry: "Try it in your browser",
    availability:
      "Local quizzes are not available yet. Paid plans and their device, backup, cancellation, refund and device-change policies will be announced before release.",
    legalNavigation: "Legal information",
    supportLink: "Support and account deletion",
    privacyLink: "Privacy policy and personal information collection statement",
    termsLink: "Terms of service",
    processingLink: "School data processing terms",
  },
  ipadGuide: {
    kicker: "iPad guide",
    title: "Add Class in Order to your Home Screen.<br/>Open it with a tap when class starts.",
    lead: "Add Class in Order to your iPad Home Screen and tap its icon to open it like an app, without entering the address or finding a browser tab each time.",
    firstUse:
      "<strong>Using Class in Order for the first time?</strong> Complete these steps first, then sign in and create your classes from the Home Screen icon.",
    reminderTitle: "After adding it, always open Class in Order from the same icon.",
    reminder:
      "<strong>The Home Screen app and Safari store data separately.</strong> Even on the same iPad, signing in to the same account does not automatically share local records between them. Avoid switching between these two ways of opening Class in Order.",
    reminderExtra:
      "Chrome and browsers inside other apps may also use separate storage. Keep using the same browser or Home Screen icon on each device.",
    stepsTitle: "Four steps to add it to your Home Screen",
    illustrativeNote: "Illustrations only. Screens and option names may vary by iPadOS version.",
    stepShareTitle: "Tap Share in Safari",
    stepShareDescription: "Open Class in Order in Safari, then tap the Share icon.",
    stepShareAlt:
      "Illustration: open Class in Order in Safari and tap Share in the toolbar. Its icon is an arrow pointing up from a square.",
    stepChooseTitle: "Select Add to Home Screen",
    stepChooseDescription: "Select Add to Home Screen in the share menu. If View More appears, tap it first.",
    stepChooseAlt:
      "Illustration: find and tap Add to Home Screen in the share menu. Some versions show View More first.",
    stepConfirmTitle: "Check the name, then tap Add",
    stepConfirmDescription:
      "Check that the name is Class in Order. If Open as Web App appears, keep it on, then tap Add.",
    stepConfirmAlt:
      "Illustration: set the name to Class in Order, keep Open as Web App on if available, and tap Add at the top right.",
    stepOpenTitle: "Keep opening the same icon",
    stepOpenDescription: "Return to your iPad Home Screen and tap the Class in Order icon. Use this icon each time.",
    stepOpenAlt: "Illustration: tap Class in Order on the iPad Home Screen. Keep using this icon to open the app.",
    websiteLabel: "Class in Order website",
    brandName: "Class in Order",
    shareLabel: "Tap Share",
    shareMenuLabel: "Website share menu",
    copyLabel: "Copy",
    bookmarkLabel: "Add Bookmark",
    addToHomeLabel: "Add to Home Screen",
    cancelLabel: "Cancel",
    addLabel: "Add",
    openAsAppLabel: "Open as Web App",
    optionalToggleHint: "Keep this on if available",
    homeScreenLabel: "iPad Home Screen",
    illustrationLabel: "Illustration",
    notesAppLabel: "Notes",
    safariLabel: "Safari",
    alwaysOpenHere: "Always open from here",
    existingTitle: "Already created classes in Safari or another browser?",
    existingDescription:
      "Adding the app to your Home Screen does not automatically move existing records. Check your classes and data when you first open the icon. <strong>If your records are missing, return to the browser you used before to check them. Avoid creating duplicate classes or clearing website data.</strong>",
    existingExtra:
      "Keep using your original browser until you have confirmed that your records are available in the new app.",
    syncNote:
      "Working across a computer and iPad? Set up cross-device sync and pairing separately. Keep using the same browser or icon on each device, and check that your latest changes have arrived before switching.",
    syncLink: "Read the cross-device sync guide",
    appleLink: "Apple instructions",
    webkitLink: "WebKit storage information",
    spotlightKicker: "iPad guide",
    spotlightTitle: "Add it to your Home Screen. Open it with a tap.",
    spotlightDescription:
      "Follow the four illustrated steps, then keep opening Class in Order from the same icon instead of switching between browsers and the Home Screen app.",
    spotlightLink: "See the Home Screen guide",
  },
  marketing: {
    syncSpotlightTitle: "Prepare on your computer. Teach on your iPad.",
    syncSpotlightDescription:
      "Choose Google Drive sync to continue with the same classes and records on your own devices. Connect once, pair by QR, then sync while Class in Order is open and online.",
    syncSpotlightPreview:
      "Device sync now includes a guided setup. Explore Drive connection and QR pairing; availability depends on the sync entry in your workspace.",
    syncGuideLink: "View the illustrated sync setup guide",
    syncHeroLink: "How do my computer and iPad work together?",
    syncComputer: "Your computer",
    syncTablet: "Your iPad",
    syncEncrypted: "Encrypted before transfer",
    syncSampleClass: "2A · Classroom records",
    syncSampleRecord: "Participation recorded",
    syncDiagramCaption: "Illustration · One teacher’s private devices continue through an authorised Drive",
    syncScreenshotOpen: "Enlarge: {{title}} (new tab)",
    syncScreenshotAlt: "Actual Class in Order screenshot of {{title}}, using fictional data",
    syncScreenshotEnlarge: "Select image to enlarge",
    syncGuideNavigation: "Sync guide sections",
    syncBrowserTitle: "Use the same browser or icon on each device",
    syncBrowserDescription:
      "Choose the browser or Home Screen icon you will use on each device, complete sync setup there, and keep using it. The sync flow has been tested in Chrome; full real-device sync verification for Safari and the Home Screen app is still pending.",
    syncIpadGuideLink: "iPad Home Screen and browser guidance",
    syncGuidePreviewNotice:
      "This is a preview of the setup flow. Sync is not available to general users in this build. Follow these steps once Sync settings appears in your workspace.",
    syncOverviewKicker: "How your records travel",
    syncOverviewTitle: "The same records, across your own devices.",
    syncOverviewDescription:
      "Records are saved locally first. Once enabled, supported classes, students, classroom records, homework, attendance, seating and tool settings are encrypted and sent to your chosen Drive app space. Class in Order does not read other Drive files.",
    syncFirstTitle: "Set up your first device",
    syncFirstLead:
      "Start on the device that holds your classes and records, and download a current encrypted backup first. These are actual desktop-browser screenshots with fictional accounts. Device names and layouts may vary. Select an image to enlarge it.",
    syncFirstStep1Title: "Open sync settings",
    syncFirstStep1:
      "After initial class setup, choose Set up device sync, or open Sync settings in the workspace header later. Select Remember this private device, then Enable Google Drive sync.",
    syncFirstStep2Title: "Authorise Google Drive",
    syncFirstStep2:
      "Select Prepare Google Drive authorization, then Open Google authorization. Choose the Google account that will store your sync data and grant access. Return to the original Class in Order tab and select Check authorization again.",
    syncFirstStep3Title: "Check the account and connect",
    syncFirstStep3:
      "Check the Class in Order account and the Drive used for sync. They may differ. Confirm the chosen Drive, select the confirmation box and connect it. Once Google Drive is connected, wait for the initial sync to finish.",
    syncOptionalNote:
      "Sync is optional. Choose Set up later to keep working locally. You can open settings from the header later without creating your classes again.",
    syncPairTitle: "Add another device",
    syncPairLead:
      "Open the same Class in Order website on both devices and keep them online. Sign in to the same Class in Order account on the new device. An existing sync opens pairing before class setup. Pairing codes are concealed in these screenshots and cannot be scanned.",
    syncPairStep1Title: "Show QR on the computer; scan on iPad",
    syncPairStep1:
      "On the connected computer, open Sync settings, Connect another device, then Show pairing QR on this device. On the iPad pairing screen, select Scan QR from another device and allow camera access. You can also swap which device displays and scans.",
    syncPairStep2Title: "Approve on the original device",
    syncPairStep2:
      "Check the joining device’s name on the original device and approve pairing. Each new device pairs once by QR; Touch ID, a PIN and managing sync keys yourself are not required.",
    syncPairStep3Title: "Download, then check a record",
    syncPairStep3:
      "Return to the iPad, select Remember this private device, then Download and connect existing sync. Keep the page open until download finishes. Open an existing class and check a recently changed record.",
    syncQrNote:
      "Use pairing QR codes only between your own devices. Do not share or save screenshots of them. Generate a new code if it expires. If the camera is unavailable, swap which device displays the code and which one scans it.",
    syncDailyKicker: "Everyday use",
    syncDailyTitle: "Check once before switching devices.",
    syncDailyLead:
      "After setup, saved changes sync while Class in Order is open and online. Sync is not guaranteed while the browser is closed or the device is asleep.",
    syncDailySave: "Finish editing on the original device and wait for Saved on this device.",
    syncDailyWait: "Keep Class in Order open and online until Waiting to sync changes to This device is synced.",
    syncDailyRead:
      "Open the same class and record on the other device. Confirm the exact change has arrived before continuing. A status label alone does not confirm receipt on another device.",
    syncHelpTitle: "If setup needs attention",
    syncHelpLocalTitle: "The new device already has local records?",
    syncHelpLocal:
      "Pairing stops and retains local data; two existing datasets are not merged automatically. Continue on the original device, download an independent encrypted backup and contact support. Do not clear website data or repeatedly pair again.",
    syncHelpConnectionTitle: "Pending sync or a reconnection notice?",
    syncHelpConnection:
      "Keep Class in Order in the foreground and online on both devices. Open Sync settings and retry the connection or sync. If authorization is requested, choose the originally linked Drive. Check on the original device until the change arrives elsewhere; do not clear data.",
    syncHelpConflictTitle: "Versions need a choice?",
    syncHelpConflict:
      "Open Sync settings, review each version and choose what to keep. Confirm the result on the other device. Resolve version differences before editing the same record on both devices again.",
    syncHelpBackupTitle: "Do I still need backups after syncing?",
    syncHelpBackup:
      "Yes. Sync transfers edits and deletions, so keep independent encrypted backups. Store the file and password separately and retain at least one paired device with its website data. If every paired device loses its website data, Google sign-in alone cannot decrypt the cloud content.",
    pointSyncAvailable: "Continue on computer and iPad",
    dataTeaserTitleSynced: "Saved locally.<br/>Ready to continue across devices.",
    dataTeaserDescriptionSynced:
      "Classroom records are saved on your own device first. Only when you choose Google Drive sync are supported records encrypted and transferred to your authorised Drive.",
    dataTeaserDetailSynced: "Pair each private device once by QR. Keep independent encrypted backups alongside sync.",
    faqStorageAnswerSynced:
      "Records are saved in the current browser first. If you choose Google Drive sync, supported records are encrypted on the device before transfer to your authorised Drive app space. Other Drive files are not read. Sign-in alone does not upload classroom records.",
    faqSyncAnswerAvailable:
      "Yes. Eligible Free Trial accounts can choose Google Drive sync, then sign in to the same Class in Order account on another private device, pair by QR and download. Sign-in alone does not transfer records. Complete pairing and check the latest change on the receiving device. See the Device sync page for setup.",
    faqQuizAnswerSynced:
      "Online quizzes and AI question generation are not available. Eligible Free Trial accounts can choose Google Drive device sync after setup and pairing. See the Device sync page.",
    dataPageDescriptionSynced:
      "Learn about local record storage, optional Google Drive device sync, account connections and independent encrypted backups.",
    dataPageLeadSynced:
      "Student and classroom records are saved locally first. Choose Google Drive sync to continue on your own computer and iPad. Sign-in, sync and independent backups each serve a different purpose.",
    homePageTitle: "Class in Order | Your classroom and class management workspace",
    homeNav: "Home",
    dataNav: "Data and backups",
    guideNav: "User guide",
    ipadNav: "iPad guide",
    ipadPageTitle: "Class in Order | iPad guide",
    ipadPageDescription:
      "Follow four illustrated steps to add Class in Order to your iPad Home Screen and learn why the Home Screen app and Safari store local records separately.",
    syncNav: "Device sync",
    siteNavigation: "Site navigation",
    entryNavigation: "Trial entry points",
    footerNavigation: "Site information",
    heroKicker: "For subject and homeroom teachers",
    heroTitle: "Turn classroom records<br/><strong>into informed follow-up.</strong>",
    heroDescription:
      "Organise responses, homework scores, attendance and class arrangements in one workspace. Review the class picture or individual records to support teaching and follow-up.",
    heroScreenLabel: "Class in Order · Classroom and class management workspace",
    heroAlt: "Current Class in Order response seat cards, answer summaries and teaching tools (fictional Demo data)",
    heroCaption: "Actual product screen · Fictional demo data",
    heroLink: "Explore Class in Order",
    pointLanguage: "Record on your iPad as you teach",
    pointRoles: "Keep teaching and class tasks together",
    pointDevices: "Export grades for your reports",
    pointStorage: "Student data stays on your device",
    missionKicker: "The everyday work behind Class in Order",
    missionTitle: "A teacher's portable toolbox.<br/>Records that follow your lesson.",
    missionDescription: "Take your iPad into class and record observations as you move among your students.",
    missionBenefit: "Less reconstructing the lesson from memory afterwards.",
    missionTeaching: "Subject teachers",
    missionTeachingDescription: "Switch classes and continue with the day's teaching and coursework.",
    missionHomeroom: "Homeroom teachers",
    missionHomeroomDescription: "Arrange seating and manage your class's everyday needs.",
    teachingToolsTitle: "Teaching tools",
    teachingToolsDescription: "Record participation, follow up homework and organise grades.",
    homeroomToolsTitle: "Homeroom tools",
    homeroomToolsDescription: "Manage class tasks and keep track of students' daily needs.",
    captureTab: "Classroom records",
    captureTitle: "Record by seat.<br/>See answer totals immediately.",
    captureDescription:
      "Tap student seats to record responses alongside missing books and notes, and comments. Review participation for the class or explore records by student and date.",
    capturePoint1: "Record responses using seat cards",
    capturePoint2: "Automatic totals for responses and days without books or notes",
    capturePoint3: "View student daily records and notes",
    captureAlt:
      "Class participation overview with responses, days with missing books and notes, and student filters (fictional Demo data)",
    gradesTab: "Homework follow-up",
    gradesTitle: "Missing submissions and marking.<br/>See both in one place.",
    gradesDescription:
      "Record submission status and scores for homework, tests and dictation. Coursework overview brings together missing, unmarked and unrecorded work to guide your next step.",
    gradesPoint1: "Record on-time, missing and late submissions",
    gradesPoint2: "Enter scores by student and review saved records",
    gradesPoint3: "Open follow-up items from Coursework overview",
    gradesAlt: "Coursework overview with marking, missing work and recent assessments (fictional Demo data)",
    reportsTab: "Grade reports",
    reportsTitle: "Scores come together automatically.<br/>Export them to continue your work.",
    reportsDescription:
      "Saved homework, test and dictation scores form a grade table. Filter coursework or students, then export Excel or CSV for your own reports. Export PDF for reading or printing.",
    reportsPoint1: "Class scores and submission status in one table",
    reportsPoint2: "Export Excel or CSV for your spreadsheets",
    reportsPoint3: "Export PDF or print grade reports",
    reportsAlt: "Grade table export preview with Excel, CSV, PDF and JSON options (fictional Demo data)",
    termsTab: "Term comparison",
    termsTitle: "From daily records<br/>to changes across the term.",
    termsDescription:
      "Review saved coursework by term, compare follow-up needs and expand a student's monthly changes. Connect everyday records to guide later support.",
    termsPoint1: "View by term and coursework type",
    termsPoint2: "Compare follow-up needs across terms",
    termsPoint3: "Expand student term and monthly records",
    termsAlt: "Teaching class term comparison, filters and student follow-up rates (fictional Demo data)",
    groupsTab: "Picker and groups",
    groupsTitle: "Pick students and form groups<br/>from the same class roster.",
    groupsDescription:
      "Use your roster to pick students or create groups during class. Avoid repeat selections within a round and group by size or number of groups.",
    groupsPoint1: "Random selection without repeats in a round",
    groupsPoint2: "Create groups by size or group count",
    groupsPoint3: "Review selection history and group lists",
    groupsAlt: "Teaching class groups created by group size (fictional Demo data)",
    attendanceTab: "Attendance",
    attendanceTitle: "Take attendance by seat.<br/>See the class picture immediately.",
    attendanceDescription:
      "Tap students on your seating chart to mark them present or absent, with automatic saving. Class totals update as you go, and past dates remain available.",
    attendancePoint1: "Toggle present or absent on seat cards",
    attendancePoint2: "See class attendance totals immediately",
    attendancePoint3: "Autosave each change and review attendance by date",
    attendanceAlt: "Homeroom attendance by seat, totals and autosave status (fictional Demo data)",
    missingTab: "Missing homework",
    missingTitle: "Follow up missing homework<br/>across subjects in one place.",
    missingDescription:
      "Record students with missing work by subject and task. Select by class number or seat, then update records when work is handed in to see who still needs follow-up.",
    missingPoint1: "Organise missing work by subject and task",
    missingPoint2: "Select students by class number or seat",
    missingPoint3: "Update submissions and follow up outstanding work",
    missingAlt: "Homeroom missing-work records by subject and daily task (fictional Demo data)",
    homeroomTab: "Class affairs",
    homeroomTitle: "A place for class tasks.<br/>Records for later follow-up.",
    homeroomDescription:
      "Use memos for slips, materials and reminders, with selected students for follow-up. Keep events and responses in the class log alongside everyday tasks.",
    homeroomPoint1: "Organise reply slips and materials in daily memos",
    homeroomPoint2: "Record class events and follow-up actions",
    homeroomPoint3: "Review existing memos and class log entries",
    homeroomAlt: "Current homeroom class log and tools (fictional Demo data)",
    dutiesTab: "Student roles",
    dutiesTitle: "Make responsibilities clear<br/>across your class.",
    dutiesDescription:
      "Assign subject representatives, class monitors and other roles directly from the roster. Add subjects or duties for your class and save changes in one place.",
    dutiesPoint1: "Assign subject representatives and other roles",
    dutiesPoint2: "Add your own subjects and roles",
    dutiesPoint3: "Choose students directly; assignments autosave",
    dutiesAlt: "Student assignments for subject representatives, class monitors and other roles (fictional Demo data)",
    studentsTab: "Student records",
    studentsTitle: "From the class overview<br/>to each student's records.",
    studentsDescription:
      "Attendance and missing-work records form a class overview to help identify students needing attention. Search by name or class number to review dates and details for follow-up.",
    studentsPoint1: "View class attendance and missing-work summaries",
    studentsPoint2: "Find students by name or class number",
    studentsPoint3: "Review attendance, missing work and recent records",
    studentsAlt: "Homeroom student list, class overview and missing work by subject (fictional Demo data)",
    seatingTab: "Seating chart",
    seatingTitle: "Arrange the class.<br/>Use the same chart for attendance.",
    seatingDescription:
      "Adjust seats, rows, columns and aisles, and switch between teacher and student views. Saved seating arrangements are reused for attendance.",
    seatingPoint1: "Tap or drag students to arrange and swap seats",
    seatingPoint2: "Adjust rows, columns, aisles and teacher or student views",
    seatingPoint3: "Seating arrangements autosave and carry over to attendance",
    seatingAlt:
      "Homeroom seating editor with rows, columns, view controls and student arrangements (fictional Demo data)",
    toolsKicker: "Two roles, one set of shared tools",
    toolsTitle: "Reuse your roster, seats and schedule.",
    toolSeatingSymbol: "Seats",
    toolGroupsSymbol: "Groups",
    toolCalendarSymbol: "Calendar",
    toolHistorySymbol: "Students",
    toolImportSymbol: "Import",
    toolExportSymbol: "Export",
    localLabel: "LOCAL",
    accountLabel: "ACCOUNT",
    demoChoiceLabel: "01 / DEMO",
    toolSeating: "Seating chart",
    toolSeatingDescription: "Use the same seating arrangements for classroom records and attendance.",
    toolGroups: "Random selection and groups",
    toolGroupsDescription: "Use your class roster to pick students and form groups.",
    toolCalendar: "Calendar and events",
    toolCalendarDescription: "Check class schedules and arrange homework, assessments and class events.",
    toolHistory: "Student records",
    toolHistoryDescription: "Move from the class overview to individual grades, daily records and notes.",
    toolImport: "Excel / CSV import",
    toolImportDescription:
      "Paste a student roster from Excel or import an Excel / CSV file, then check it before adding students to your class.",
    toolExport: "Export records",
    toolExportDescription: "Filter what you need, then export Excel, CSV, PDF or JSON.",
    interactionKicker: "Try it first",
    interactionTitle: "A student has answered.<br/>Try recording it.",
    interactionMobileTitle: "See how classroom<br/>records connect.",
    interactionDescription: "Tap a fictional student's seat card to change the answer count, then try resetting it.",
    interactionMobile: "Use a computer or iPad for the interactive demo. You can read the introduction on your phone.",
    dataTeaserKicker: "You keep your data",
    dataTeaserTitle: "Student records<br/>stay on your device.",
    dataTeaserDescription:
      "Free Trial rosters and classroom records stay in this device's browser and are not automatically uploaded. Signing in checks access; it does not sync devices.",
    dataTeaserDetail: "Currently supports one device and the same browser. Download encrypted backups regularly.",
    dataLink: "About storage and backups",
    faqKicker: "Before you start",
    faqTitle: "A few things you may want to know.",
    faqMore: "Full FAQ and getting started",
    faqDemoQuestion: "How do Demo and Free Trial differ?",
    faqDemoAnswer:
      "Demo uses fictional classes and students, requires no sign-in and resets on reload. Free Trial uses Google sign-in and lets you create classes and records stored in this device's browser.",
    faqDeviceQuestion: "Can I use a phone? Which devices are supported?",
    faqDeviceAnswer:
      "Use the classroom workspace on a computer or iPad. Phones can display the website introduction and guides. On iPad, add Class in Order to your Home Screen and keep opening the same icon. If you already have records, read the data reminder in the iPad guide first.",
    faqStorageQuestion: "Is student data uploaded to the cloud?",
    faqStorageAnswer:
      "Current Free Trial rosters and classroom records stay in the device's browser and are not automatically uploaded. Google sign-in and access checks need a connection. Account services process necessary account details, IP addresses and timestamps. See Data and backups and the privacy policy.",
    faqSyncQuestion: "Will signing in on another device show my records?",
    faqSyncAnswer:
      "Records do not appear automatically. The app currently supports one device and the same browser. Signing in does not sync classroom data. Download an encrypted backup before changing device, then restore it in the new environment. Restoring replaces that environment's existing data.",
    faqPriceQuestion: "Do I need to pay now?",
    faqPriceAnswer:
      "Free Trial currently requires no credit card. Paid plans, prices and related policies have not been announced and will be explained before release.",
    faqInternetQuestion: "Do I always need an internet connection?",
    faqInternetAnswer:
      "Free Trial checks access online at startup and when returning to the app. After a network failure, saving remains available for 10 minutes after the last successful check, then becomes read-only. Viewing, export and encrypted backups remain available. Expired or revoked access immediately becomes read-only.",
    faqBackupQuestion: "How do backups work? What if I clear my browser?",
    faqBackupAnswer:
      "Download encrypted backups regularly and keep files and passwords separately. Clearing site data, removing browser data or switching browsers may make local records unavailable. Backups are not automatic cloud sync. Confirm carefully before restoring, because existing data is replaced.",
    faqImportQuestion: "Can I reuse Excel records?",
    faqImportAnswer:
      "Supported Excel or CSV records can be previewed, mapped and checked before importing. Not every file can be imported unchanged. Student and classroom records can also be filtered and exported.",
    faqRealQuestion: "Can I use Free Trial in real classes?",
    faqRealAnswer:
      "Yes, subject to school rules and applicable data protection requirements. You manage device access and backups, and keep rosters and classroom records locally.",
    faqQuizQuestion: "Are online quizzes, AI questions or cloud sync available?",
    faqQuizAnswer:
      "These features are not available yet. This website shows the currently available classroom, coursework and class management tools. The introduction will be updated when features are released.",
    ctaKicker: "Start with one class",
    ctaTitle: "Try the workflow, then use it in your classroom.",
    ctaDescription:
      "Demo includes fictional rosters and records. See whether it fits your work without preparing your own data first.",
    footerDescription: "Your classroom and class management workspace.",
    footerDeviceNote: "Use a computer or iPad for the workspace. Phones can view the website introduction.",
    dataPageTitle: "Class in Order | Data and backups",
    dataPageDescription:
      "Learn about Free Trial local storage, account connections, encrypted backups and single-device use.",
    dataPageKicker: "Data and backups",
    dataPageTitleText: "Records stay on your device.<br/><strong>Know how they are stored.</strong>",
    dataPageLead:
      "Class in Order currently offers a device-local Free Trial. You keep student and classroom records; account sign-in and record storage serve different purposes.",
    dataFlowDevice: "Your device and browser",
    dataFlowRecords: "Rosters · Classroom · Coursework · Class affairs records",
    dataFlowBackup: "Manually download encrypted backups",
    dataFlowAccount: "Account and access",
    dataFlowAccountDetail: "Google sign-in and access checks; no student or classroom records sent",
    dataFlowCaption: "How Free Trial currently stores data",
    dataAccountTitle: "Sign-in needs a connection. Classroom data stays local.",
    dataAccountDescription:
      "Google handles sign-in, while account and access services process necessary authorisation details. These connections do not upload rosters or classroom records. Services still receive IP addresses and connection times.",
    dataChecklistTitle: "Before changing device or clearing data",
    dataChecklist1: "Download a current encrypted backup and confirm the file is saved.",
    dataChecklist2: "Keep the backup and its password separately, preferably beyond the same device.",
    dataChecklist3: "Check the destination before restoring; it replaces existing local data.",
    dataReadOnlyTitle: "Keep your records when a connection fails.",
    dataReadOnlyDescription:
      "Connection or access problems do not automatically delete local data. You can still view, export and download encrypted backups while read-only.",
    policyLink: "Read the full privacy policy",
    syncPageTitle: "Class in Order | Device sync",
    syncPageDescriptionAvailable:
      "Free Trial users can choose Google Drive sync and pair their own computer and iPad to continue with classes and classroom records.",
    syncPageDescription:
      "Device sync is in development. Class in Order currently supports one device and the same browser. Learn how to use it now and what is planned.",
    syncStatusAvailable: "Optional in Free Trial",
    syncLeadAvailable: "Prepare on your computer and teach on your iPad with the same classes and classroom records.",
    syncCurrentTitleAvailable: "Set up once, then sync automatically.",
    syncCurrentDescriptionAvailable:
      "Choose Google Drive sync and pair other private devices once using a QR code. Saved changes sync while Class in Order is open and online.",
    syncPlanTitleAvailable: "Encrypted data in your own Drive.",
    syncPlanDescriptionAvailable:
      "Sync data are encrypted on your device before being stored in your authorized Drive app space. Other Drive files are not accessed. Wait for sync before switching devices and keep independent backups.",
    syncStatus: "In development",
    syncLead: "Making teachers' work easier is at the heart of Class in Order.",
    syncCurrentTitle: "Currently supports one device",
    syncCurrentDescription:
      "Use the same device and browser. Classroom and class affairs records stay in that browser and do not automatically sync, even when you sign in to the same account elsewhere.",
    syncDataLink: "About current storage and backups",
    syncPlanTitle: "Continue your work across devices",
    syncPlanDescription:
      "We understand that teachers move between computers and iPads. We are developing device sync so you can continue classroom and class management work across devices, and aim to offer it as soon as possible.",
    guidePageTitle: "Class in Order | User guide",
    guidePageDescription:
      "Getting started with Demo and Free Trial, plus questions about devices, backups, imports, connections and pricing.",
    guideKicker: "User guide",
    guideTitle: "Start with one everyday task.<br/><strong>Start with Class in Order.</strong>",
    guideLead:
      "See how recording works before deciding how to use it. Explore Demo or sign in to Free Trial to create your own classes.",
    guideStepsTitle: "Get started with your class.",
    guideStep1Title: "Set up your classes",
    guideStep1Description: "Sign in with Google and add your homeroom and teaching classes.",
    guideStep2Title: "Import the roster",
    guideStep2Description: "Review the roster before importing it into your class.",
    guideStep3Title: "Arrange seats",
    guideStep3Description: "Set up seating and start recording classroom and class affairs information.",
    faqAllTitle: "Frequently asked questions",
  },
  classSetupStatus: {
    notImported: "Roster not imported",
    needsSeating: "Seats not arranged",
    complete: "Setup complete",
    cardRegion: "{{name}} setup status",
    studentCount: "{{count}} students",
    studentCount_one: "{{count}} student",
    studentCount_other: "{{count}} students",
    classEntry: "{{name}}, {{count}} students",
    classEntry_one: "{{name}}, {{count}} student",
    classEntry_other: "{{name}}, {{count}} students",
    importRoster: "Import roster",
    arrangeSeating: "Arrange seats",
    classAction: "{{name}}: {{action}}",
    manageClasses: "Manage classes",
    addClass: "New class",
    emptyRegion: "Class setup",
    emptyTitle: "{{name}} has no roster yet",
    emptyDescription: "Import this class's roster to start using teaching and homeroom tools.",
    followUpRegion: "Continue setup",
    classComplete: "{{name}} setup complete",
    tutorialComplete: "Basic tutorial for {{name}} complete",
    continueCurrentSeating: "The roster is imported. Continue arranging the remaining students' seats.",
    otherClasses: "Other classes:",
    missingRosterCount: "{{count}} classes need rosters",
    missingRosterCount_one: "{{count}} class need rosters",
    missingRosterCount_other: "{{count}} classes need rosters",
    missingSeatingCount: "{{count}} classes need seats",
    missingSeatingCount_one: "{{count}} class need seats",
    missingSeatingCount_other: "{{count}} classes need seats",
    canSetUpLater: ". Continue setup now or come back later.",
    noOtherClasses: "You are ready to begin. Add more classes in Manage classes at any time.",
    nextClass: "Set up next class",
    startUsing: "Start using",
    waitForSave: "Wait for the current changes to finish saving.",
    leaveUnsaved: "You have unsaved changes. Discard them and switch class or leave this screen?",
  },
  classSetup: {
    title: "Set up my classes",
    trial: "Free Trial · Settings stay in this browser",
    intro: "Tell us which classes you teach so we can organise your workspace.",
    steps: ["Homeroom class", "Teaching classes", "Review"],
    stepCount: "Step {{step}} of 3",
    homeTitle: "Which is your homeroom class?",
    homeIntro: "Enter one class. If you are not a homeroom teacher this year, select the option below.",
    hasHome: "I am a homeroom teacher",
    noHome: "I am not a homeroom teacher this year",
    homeLabel: "Homeroom class",
    homePlaceholder: "e.g. 3C",
    teachingTitle: "Which classes and subjects do you teach?",
    teachingIntro: "Add them one at a time. Select a class already entered, or add more later.",
    classLabel: "Class",
    subjectLabel: "Subject",
    rowClass: "Class in row {{row}}",
    rowSubject: "Subject in row {{row}}",
    rowRemove: "Remove row {{row}}",
    classPlaceholder: "e.g. 2A",
    subjectPlaceholder: "e.g. Citizenship and Social Development",
    addTeaching: "＋ Add teaching class",
    noTeaching: "No teaching classes at present",
    sharedNote: "The same class shares its roster and seating chart. Subject records are saved separately.",
    reviewTitle: "Your class entry points",
    reviewIntro: "Your home screen will have two sections for quick access to your work.",
    homeroomGroup: "Homeroom class",
    teachingGroup: "Teaching classes",
    homeTools: "Attendance · Missing work · Class affairs",
    teachingTools: "Participation · Coursework · Grades",
    noHomeroom: "No homeroom class this year",
    noTeachingSummary: "No teaching classes added yet",
    nextSetup: "Next: import student rosters",
    nextSetupHint:
      "Import once per class. Open a class, then use Administration to import its roster and arrange seats. Existing classes and records are retained.",
    next: "Next",
    back: "Back",
    finish: "Confirm and start",
    saving: "Saving…",
    saveFailed: "Could not save classes. Try again.",
    draftSaving: "Saving draft…",
    draftSaved: "Draft autosaved",
    draftSaveFailed: "This browser could not save the draft. Finish setup before refreshing.",
    manualSetup: "Add classes individually",
    openSetup: "Set up my classes",
    errorHome: "Enter your homeroom class first.",
    errors: {
      empty: "Add at least one homeroom or teaching class.",
      teachingChoice: "Enter teaching classes and subjects, or select 'No teaching classes at present'.",
      incomplete: "Complete every class and subject, or remove incomplete rows.",
      duplicate: "Add each class and subject only once. Remove duplicate rows.",
      existingHomeroom:
        "You already have a homeroom class. Use it, or deactivate it in Administration before replacing it.",
      ambiguous:
        "Several classes share this name. Check their names in Administration to avoid linking the wrong roster.",
    },
  },
  randomGroups: {
    mode: "Grouping method",
    bySize: "Students per group",
    byCount: "Number of groups",
  },
  responseDraw: {
    pick: "Pick a student",
    again: "Pick another",
    remaining: "{{remaining}} / {{total}} remaining",
    result: "Seating selection result",
    student: "No. {{number}} {{name}}",
    selected: "Selected",
    unseated: "No assigned seat",
    record: "Record one response",
    recorded: "Response recorded",
  },
  drawHistory: {
    clear: "Clear history",
    clearConfirm:
      "Clear all {{count}} selections for '{{name}}'? This cannot be undone. The current round and selection settings are retained.",
    clearConfirm_one:
      "Clear all {{count}} selection for '{{name}}'? This cannot be undone. The current round and selection settings are retained.",
    clearConfirm_other:
      "Clear all {{count}} selections for '{{name}}'? This cannot be undone. The current round and selection settings are retained.",
    clearFailed: "Could not confirm the result. Refresh and check the records.",
    resetMode: "Reset roster",
    dailyMode: "Reset daily",
    completeMode: "Reset after everyone is selected",
    title: "Selection history",
    count: "{{count}} selections",
    count_one: "{{count}} selection",
    count_other: "{{count}} selections",
    description: "Each result and its Hong Kong time are saved, even after the selection roster resets.",
    time: "Selection time (Hong Kong)",
    number: "Student number",
    name: "Name",
    empty: "No selections yet. Results are saved after each selection.",
    pagination: "Selection history pages",
    previous: "Previous",
    next: "Next",
    page: "Page {{page}} of {{pages}}",
    saveFailed: "Could not save the selection. Try again.",
  },
  studentPagination: {
    view: "Attendance view",
    list: "Class number list",
    seating: "Seating chart",
    records: "Student record pages",
    directory: "Roster pages",
    routine: "Daily task student pages",
    memos: "Memo student pages",
    unseated: "Unseated student pages",
    submissions: "Test submission pages",
    previous: "Previous",
    next: "Next",
    page: "Page {{page}}",
    position: "Page {{page}} of {{pages}}",
  },
  attendanceRegister: {
    present: "Present",
    absent: "Absent",
    studentAction: "No. {{number}} {{name}}{{gender}}: {{current}}. Select to mark {{next}}",
    saving: "Saving…",
    saved: "Saved",
    ready: "Changes save automatically",
    failed: "Could not save attendance.",
    savedDate: "Attendance for {{date}} saved",
  },
  quickBackup: {
    action: "Backup",
    title: "Back up current data",
    demoTitle: "Back up demo data",
    demoFilename: "Class-in-Order-demo-backup",
    password: "Backup password (at least 12 characters)",
    sessionHint:
      "You need this password to restore. While this page stays open, select Backup again to download the latest data directly.",
    download: "Back up now",
    busy: "Backing up…",
    cancel: "Cancel",
    cancelBackup: "Cancel backup",
    downloaded: "Encrypted backup of the latest data downloaded.",
    demoDownloaded: "Encrypted backup of fictional demo data downloaded.",
    canceled: "Backup cancelled. Existing data is unchanged.",
    failed: "Backup failed. Try again.",
    tooLarge: "The backup exceeds the current 100 MB limit.",
    saveFirst: "Save your current changes and wait for saving to finish before backing up.",
  },
  backupFolder: {
    chooseAction: "Choose a folder and save encrypted backup",
    writing: "Writing the encrypted backup to the selected folder…",
    explanation:
      "You can choose a synced folder such as iCloud Drive or Proton Drive. Your installed sync app handles cloud uploads; check its sync status yourself.",
    newFile: "A new file is created each time, retaining earlier backups. Folder permission is not remembered.",
    unsupported:
      "This browser cannot select folders directly. Download the encrypted backup, then move it to your chosen sync folder.",
    saved:
      "Encrypted backup saved in '{{folder}}': {{filename}}. Confirm your sync app has uploaded it and keep the password separately.",
    canceled:
      "Cancelled. Classroom data and earlier backups are unchanged. If writing had started, check for a newly created backup file.",
    permission: "Could not obtain folder write permission. Choose the folder again or download the backup instead.",
    collision: "Could not create a new backup filename. Earlier files were not overwritten. Choose the folder again.",
    invalid: "The backup format or size is invalid. Nothing was written to the folder.",
    write:
      "Could not complete or verify writing. Check folder space and the new file, then choose the folder again. Earlier backups were not overwritten.",
  },
  cloudMigration: {
    title: "Import previous version data",
    intro:
      "Choose a JSON export from the previous classroom workspace (up to 50 MB). It is checked on this device without connecting to or updating the old server.",
    archive:
      "Previous quiz data is archived and included in encrypted backups. Quiz tools are not available in this version.",
    file: "Previous JSON export file",
    check: "Check export",
    working: "Checking or importing…",
    previewTitle: "Review before import",
    source: "Source workspace",
    target: "Local workspace",
    exportedAt: "Export date",
    checksum: "File fingerprint (SHA-256)",
    counts: "Compare record counts",
    collection: "Data category",
    current: "Current local data",
    incoming: "Export file",
    replaceNotice:
      "Importing replaces current data in this browser, retaining record IDs and relationships while mapping workspace IDs to this browser. Tool visibility and local access settings are retained. Download an encrypted backup first.",
    acknowledge:
      "I have backed up current data and confirm replacing it with the previous workspace, mapped to this browser.",
    import: "Replace and import",
    confirm: "Replace current browser data with this export? Keep an encrypted backup of current data first.",
    success: "Previous data imported. Check your classes and records, then download a new encrypted backup.",
    failed: "Import failed. Current data is retained.",
    tooLarge: "The export exceeds the 50 MB limit.",
    collections: {
      classes: "Classes",
      students: "Students",
      dailyRecords: "Responses and missing books and notes",
      homeworkAssignments: "Coursework tasks",
      homeworkRecords: "Student coursework records",
      calendarEvents: "Calendar events",
      homeroomRecords: "Class records",
      attendanceRecords: "Attendance",
      homeroomHomeworkRecords: "Homeroom missing homework",
      studentRoutineItems: "Daily tasks",
      seatingCharts: "Seating charts",
      quizzes: "Archived question sets",
      quizSessions: "Archived quiz sessions",
      quizResponses: "Archived responses",
      quizAttempts: "Archived response drafts",
      personalNotes: "Personal notes",
      personalNotePages: "Handwritten pages",
    },
  },
  rosterAdmin: {
    deleteEmptyClass: "Delete empty class",
    deleteEmptyNote:
      "This cannot be undone. Only classes with no students, records or shared-roster links can be deleted.",
    deleteEmptyName: "Enter the class name to confirm",
    deletingEmpty: "Deleting…",
    emptyBlocked: "This class has students, records or shared-roster links. It was retained.",
    emptyChanged: "The class name has changed. Select and confirm it again.",
    emptyFailed: "Could not delete the class. Try again later.",
    sourceClass: "Original class",
    invalidSourceClass: "Enter a valid original class (up to 40 characters).",
    addStudentEntry: "Add an individual student",
    addStudentHint: "New arrivals / occasional additions",
    studentCount: "{{count}} students",
    addStudent: "Add student",
    adding: "Adding…",
    addClass: "Add class",
    chooseClass: "Choose class",
    number: "Class number",
    name: "Name",
    gender: "Gender",
    numberLabel: "New student class number",
    nameLabel: "New student name",
    genderLabel: "New student gender",
    unspecified: "Not specified",
    male: "Male",
    female: "Female",
    other: "Other",
    added: "Added No. {{number}} {{name}}",
    duplicateNumber: "Student number {{number}} is already in use. Choose another number.",
    invalidNumber: "Class number must be an integer from 1 to 999.",
    missingName: "Enter the student's name.",
    invalidGender: "Select a gender again.",
    invalidClass: "The class has changed or is inactive. Select it again.",
    saveFailed: "Could not add the student. Keep your input and try again later.",
  },
  adminRecords: {
    importTitle: "Import records",
    exportTitle: "Export records",
    intro:
      "Export across classes, terms or record types. Choose a scope and Excel, CSV, PDF or JSON. For a full backup, open Local data.",
    chooseClasses: "1. Choose classes",
    roles: {
      teaching: "Teaching classes",
      homeroom: "Homeroom classes",
    },
    role: "Class type",
    allRoleClasses: "Select all classes of this type",
    clearClasses: "Clear classes",
    noClasses: "No classes of this type yet.",
    noSelectedClasses: "No classes selected",
    noSelectedRecords: "No record types selected",
    recordsStep: "2. Choose records",
    roleRecords: "{{role}} records",
    sharedRecords: "Shared data",
    dateStep: "3. Date range",
    summary: "This export",
    selectedClasses: "Selected classes · {{count}}",
    selectedRecords: "Selected record types · {{count}}",
    selectedDates: "Record dates",
    fromOnly: "From {{date}}",
    toOnly: "Until {{date}}",
    dateRange: "{{from}} to {{to}}",
    downloadSelection: "Export {{format}} · {{classes}} classes / {{records}} types",
    formatHints: {
      xlsx: "Organise and analyse",
      csv: "General spreadsheet",
      pdf: "Read and print",
      json: "Data exchange",
    },
    descriptions: {
      roster: "Student numbers, names and details",
      answers: "Dates, student responses and notes",
      materials: "Dates, missing books and notes status, and comments",
      coursework: "Coursework, submission status and scores",
      attendance: "Dates, attendance status and notes",
      missingHomework: "Subjects, coursework and missing-work status",
      routine: "Daily tasks and student records",
      affairs: "Class events and follow-up records",
      duties: "Current roles and assigned students",
      seating: "Current seats and student arrangements",
      calendar: "Dates, events and notes",
    },
    scope: "Export scope",
    class: "Export class",
    allClasses: "All classes",
    term: "Export term",
    allDates: "All dates",
    chooseRecords: "Choose record types",
    all: "Select all records",
    clear: "Clear selection",
    format: "File format",
    download: "Export {{format}}",
    working: "Exporting…",
    downloaded: "Export downloaded.",
    failed: "Could not complete the export. Try again.",
    fileHint:
      "Excel uses separate sheets for each class and record type. CSV combines tables with source labels; PDF combines them into one document; JSON retains table fields and data. Rosters, seats and current roles are not filtered by date. JSON record exports cannot restore a full backup.",
    preview: "Export preview",
    previewHint: "Preview shows up to 10 records per page. Downloads include all records in the selected scope.",
    emptyRecords: "No records of this type in the selected scope.",
    kinds: {
      roster: "Student roster",
      answers: "Class responses",
      materials: "Missing books and notes",
      coursework: "Coursework and grades",
      attendance: "Attendance records",
      missingHomework: "Homeroom missing work",
      routine: "Daily tasks",
      affairs: "Class affairs",
      duties: "Student roles",
      seating: "Seating chart",
      calendar: "Calendar events",
    },
  },
  terms: {
    title: "Term overview",
    details: "Student record details",
    settingsTitle: "School year and terms",
    settingsIntro: "Set up once per school year for all classes. Adjust term dates to your school calendar.",
    scope: "Record term",
    all: "All dates",
    configure: "Set up terms",
    manage: "Manage terms",
    noTerms: "No terms configured. Set dates in Administration to view and compare student performance by term.",
    range: "{{start}} to {{end}} (inclusive)",
    gap: "Records outside the selected term remain available under All dates.",
    settingsEyebrow: "Shared class settings",
    schoolYear: "School year",
    name: "Term name",
    start: "Start date",
    end: "End date",
    addYear: "Add school year",
    setupYear: "Set up school year",
    closeSetup: "Close school year setup",
    setupIntro:
      "How many terms does your school have? Choose a number, then set term boundaries using your school calendar.",
    termCount: "Number of terms",
    templatePreview: "Term date template",
    templateStart: "{{name}} start date",
    templateEnd: "{{name}} end date",
    setupLater: "Set up later",
    createTerms: "Create terms",
    yearStart: "School year starting year",
    twoTerms: "Two terms",
    threeTerms: "Three terms",
    academicYearRange: "{{year}}–{{nextYear}} school year: 1 September {{year}} to 31 August {{nextYear}}.",
    addTerm: "＋ Add term",
    remove: "Remove term {{name}}",
    save: "Save term settings",
    saving: "Saving…",
    saved: "Term settings saved",
    reset: "Reset unsaved changes",
    defaults:
      "Changing a term's end date starts the next term on the following day. The school year runs from 1 September to 31 August. Save once; no repeated setup is needed.",
    removeNote: "Removing or changing a term changes only the reporting range. Student records are retained.",
    dateRule:
      "Daily records use their record date; coursework and tests use their due date; daily tasks use their event date (creation date for older data). Late submissions and scores remain in their original term.",
    errors: {
      required: "Enter school year and term names.",
      invalidDate: "Enter valid start and end dates.",
      order: "End date cannot be before start date.",
      overlap: "Term dates cannot overlap. Each term must end before the next term starts.",
      duplicate: "Duplicate term ID. Add the term again.",
      yearExists: "This school year already exists. Edit its dates below.",
      yearInvalid: "Enter a starting year between 1900 and 9998.",
      cloud: "Term settings are currently available in Demo and local versions.",
      save: "Could not save term settings. Try again.",
    },
    overviewIntro:
      "Review each student's current term and compare another term. Select a name to expand term and monthly records.",
    selected: "View term",
    compare: "Compare term",
    noCompare: "No comparison",
    compared: "{{current}} compared with {{previous}}",
    student: "Student",
    search: "Search term overview students",
    searchPlaceholder: "Class number or name",
    sort: "Sort term overview",
    byNumber: "Class number",
    byGrade: "Largest grade change first",
    category: "Comparison type",
    homeworkMissing: "Missing submission rate",
    examMissing: "Assessment absence rate",
    recordMissing: "Follow-up rate",
    examCount: "{{tasks}} recorded assessments · {{value}} absences",
    recordCount: "{{tasks}} recorded tasks · {{value}} follow-ups",
    examFollowup: "Missed assessments this term",
    recordFollowup: "Records needing follow-up this term",
    byResponse: "Largest response increase first",
    byFollowup: "Needs follow-up first",
    export: "Export term overview CSV",
    expand: "View term changes for No. {{number}} {{name}}",
    disabled: "Inactive",
    empty: "No students in this class yet.",
    missing: "No data",
    noBaseline: "No data for the comparison term",
    noCurrent: "No data for this term",
    same: "No change",
    timesUnit: "occurrences",
    up: "Up {{value}} {{unit}}",
    down: "Down {{value}} {{unit}}",
    points: "percentage points",
    perDayUnit: "per recorded day",
    perTen: "{{value}} per 10 tasks",
    perTenUnit: "per 10 tasks",
    perDay: "{{value}} per day",
    percent: "{{value}}%",
    times: "{{value}} times",
    grade: "Average grade",
    answers: "Class responses",
    materials: "Missing books and notes rate",
    attendance: "Attendance rate",
    subjectHomework: "Teaching missing-work rate",
    homeroomHomework: "Homeroom missing work",
    routine: "Daily task exceptions",
    routineDate: "Task date",
    gradeCount: "{{value}} valid scores",
    dailyCount: "{{days}} recorded days · {{value}} total",
    attendanceCount: "{{days}} attendance days · {{absent}} absent · {{late}} late",
    taskCount: "{{tasks}} recorded tasks · {{value}} missing",
    homeroomCount: "{{tasks}} class records · {{value}} missing",
    studentsCount: "{{value}} students",
    gradeUp: "Grades increased",
    responseUp: "Responses increased",
    courseworkNotes:
      "Average grades include assessments with valid scores and maxima, averaged equally as percentages. Missing-work rates use tasks with recorded status. Assessment content and weights may differ by term; score differences are for follow-up reference only.",
    followup: "Missing coursework this term",
    homeroomFollowup: "Needs follow-up this term",
    change: "Change",
    comparable: "Grades available to compare",
    history: "Performance across terms",
    monthly: "Monthly changes this term",
    month: "Month",
    notes:
      "Only saved data is compared. Grades average valid percentages equally. Responses and missing books and notes average recorded days; attendance includes lateness. Missing-work status updates when late work is submitted.",
    homeroomNote:
      "Homeroom missing-work totals count saved exception records. Daily task exceptions show the current status for tasks in that date range, rather than historical daily totals.",
    noObservation:
      "Unrecorded does not mean zero. ↑ / ↓ show increases or decreases, without combining them into an overall score.",
    outOfRange: "Choose a date within the selected term.",
    unsaved: "You have unsaved student records. Switching term will hide these changes. Continue?",
    busy: "Records are saving. Wait before switching term.",
    scopeExport: "{{name}} · {{start}} to {{end}}",
    detailTitle: "No. {{number}} {{name}} · Term changes",
  },
  answerRanking: {
    gold: "Gold",
    silver: "Silver",
    bronze: "Bronze",
    medalRank: "Cumulative answer rank {{rank}} ({{medal}})",
  },
  automaticSave: {
    status: "{{name}} save status",
    error: "{{name}} save error",
    ready: "Saves automatically when complete",
    incomplete: "Incomplete; not saved yet",
    saving: "Saving…",
    saved: "Saved",
    failed: "Could not save",
    retained: "Your changes remain on screen. Try again.",
    retry: "Retry saving",
    discard: "You have unsaved changes. Discard them and switch record?",
  },
  keyboard: {
    saveDuties: "Save roles",
    addSecondMonitor: "Add second representative for {{subject}}",
    removeSecondMonitor: "Remove second representative for {{subject}}",
    addSecond: "＋ Add second",
    removeSecond: "Remove second",
    studentActiveLabel: "No. {{number}} {{name}}: active status",
    studentEnabled: "{{name}} activated",
    studentDisabled: "{{name}} deactivated",
    studentUpdateFailed: "Could not update student status. Try again later.",
    calendarStart: "Item {{number}} start date",
    calendarEnd: "Item {{number}} end date",
    calendarTitle: "Item {{number}} title",
    calendarCategory: "Item {{number}} category",
    calendarNote: "Item {{number}} note",
    calendarSource: "View source text for item {{number}}",
    feedbackSubmitting: "Submitting feedback. Please wait…",
    deleteStudent: "Permanently delete all data for No. {{number}} {{name}}",
    decreaseAnswer: "No. {{number}} {{name}}: decrease answer count",
    studentSubmission: "No. {{number}} {{name}}: submission status",
    studentScore: "No. {{number}} {{name}}: score",
    emptySeat: "Row {{row}}, column {{column}}: empty seat",
    emptySeatSelected: "Row {{row}}, column {{column}}: empty seat; place selected student",
    saveTopGroup: "Top save actions",
    saveBottomGroup: "Bottom save actions",
    saveToday: "Save today's records",
    recordSaving: "Saving…",
    recordSaved: "Classroom records saved",
    recordSaveToast: "Today's student records saved",
    recordUnsaved: "Unsaved changes",
    recordReady: "Ready to record",
    recordSaveFailed: "Could not save classroom records.",
    recordDraftRetained: "Your changes remain on screen. Try again.",
    recordSaveStatus: "Classroom record save status",
    recordRetry: "Retry saving",
    recordSaveError: "Classroom record save error",
    answersRegion: "Answer records",
    materialsRegion: "Missing books and notes records",
    assignmentEditor: "{{title}}: student records",
    assignmentSaveStatus: "Student coursework save status",
    assignmentSaveError: "Student coursework save error",
    saveAssignment: "Save student records",
    localDataRegion: "Local data and backups",
    backupDownloadAction: "Download encrypted backup",
    backupRestoreAction: "Restore and replace existing data",
    backupProgressStatus: "Backup progress",
    localDataResult: "Local data result",
    localDataError: "Local data error",
    storageCapacityStatus: "Device storage status",
  },
  classroomPerformance: {
    eyebrow: "Participation · Engagement and preparation",
    title: "Class participation overview",
    description:
      "Review this term's responses and missing books and notes records. Select a student for daily details.",
    actions: {
      materials: "Record missing books and notes",
      answers: "Record class responses",
      terms: "Compare terms",
    },
    filters: {
      region: "Participation date filters",
      customDates: "Custom dates",
      activeRange: "Current range: {{start}} to {{end}}",
      openStart: "Earliest record",
      openEnd: "Latest record",
      startDate: "Start date",
      to: "to",
      endDate: "End date",
      allDates: "Reset date range",
      recordDays: "{{count}} recorded days",
      invalidRange: "Start date cannot be after end date. Adjust the range.",
    },
    method:
      "Shows saved records for active students only. Unrecorded days do not count as zero responses. Missing books and notes count once per day.",
    empty: {
      title: "No classroom records in this range",
      description: "Save responses or missing books and notes records to review them in the student list.",
    },
    times: "{{count}} times",
    times_one: "{{count}} time",
    times_other: "{{count}} times",
    students: {
      title: "Students",
      tableLabel: "Student classroom records",
      historyLabel: "Daily classroom history",
      filtersLabel: "Student record filters",
      filters: {
        all: "All students",
        materials: "Missing books and notes",
        noResponses: "No responses",
      },
      observedDays: "{{count}} recorded days",
      missingDays: "{{count}} days",
      missingDays_one: "{{count}} day",
      missingDays_other: "{{count}} days",
      latestMissing: "Latest: {{date}}",
      latestNote: "Latest note",
      description: "Select a student to review daily responses, missing books and notes, and comments.",
      searchLabel: "Search class participation students",
      searchPlaceholder: "Search name or student number",
      sortLabel: "Sort class participation",
      sort: {
        number: "Student number",
        answers: "Most responses first",
        materials: "Most days with missing books and notes first",
      },
      student: "Student",
      answers: "Responses",
      missing: "Days with missing books and notes",
      lastResponse: "Latest response",
      view: "View participation for No. {{number}} {{name}}",
      details: "Participation details for No. {{number}} {{name}}",
      unrecorded: "No records yet",
      noResponse: "No responses recorded",
      date: "Date",
      response: "Responses",
      materials: "Books and notes preparation",
      note: "Note",
      missingMaterials: "Missing books and notes",
      noMissingMaterials: "No missing books and notes marked",
      noHistory: "No records for this student in the selected date range.",
      empty: "No matching students.",
    },
  },
  gradeOverview: {
    dashboard: {
      title: "Coursework overview",
      passPlaceholder: "—",
      followupRegion: "Coursework follow-up list",
      openTable: "Open grade table",
      compareTerms: "Compare terms",
      pending: "To mark",
      missing: "Missing / absent",
      unrecorded: "Unrecorded",
      recordsUnit: "records",
      attention: "Needs follow-up",
      taskCount: "{{count}} tasks",
      taskCount_one: "{{count}} task",
      taskCount_other: "{{count}} tasks",
      openRecord: "Open {{title}}",
      pendingCount: "{{count}} to mark",
      missingCount: "{{count}} missing",
      absentCount: "{{count}} absent",
      unrecordedCount: "{{count}} unrecorded",
      clear: "No items need follow-up",
      concerns: "Grades needing attention",
      passLine: "Pass mark",
      invalidPassLine: "Pass mark must be between 0 and 100%.",
      noPassLine: "Pass mark not set",
      noConcerns: "No marked records below the pass mark",
      belowCount: "{{count}} below pass mark",
      recent: "Recent assessments",
      average: "Average score",
      assessed: "{{count}} marked",
      noMaximum: "No maximum score",
      viewScores: "View scores",
      noEvaluations: "No test or dictation records yet",
    },
    table: {
      title: "Grade table",
      term: "View term",
      number: "Student number",
      name: "Name",
      maximum: "Out of {{score}}",
      scope: "{{students}} students · {{assignments}} tasks",
      categoryFilter: "Category",
      backToAll: "Back to all tasks",
      region: "Grade table scroll area",
      unscored: "Unmarked",
      unrecorded: "Unrecorded",
      noAssignments: "No coursework in the selected scope",
    },
    compareTerms: "Compare term grades",
    eyebrow: "Coursework and grades",
    title: "Coursework grade overview",
    recordsTitle: "Coursework record overview",
    description: "Compare tasks, review marking progress and identify students needing follow-up.",
    recordAction: "Record coursework scores",
    categoryLabel: "Grade category",
    categories: {
      all: "All tasks",
      homework: "Homework",
      assessment: "Tests",
      dictation: "Dictation",
    },
    stats: {
      average: "Class average score",
      assignmentCount: "Total tasks",
      unscored: "Unmarked records",
      averageNote: "Each valid score is equally weighted as a percentage",
      progress: "Marking progress",
      progressValue: "{{scored}}<total> / {{possible}}</total>",
      progressNote: "Tasks with a maximum score · Exemptions excluded",
      assessed: "Students with scores",
      assessedValue: "{{assessed}}<total> / {{total}}</total>",
      assessedNote: "At least one valid score",
      missing: "Missing submissions",
      examMissing: "Missed assessments",
      followup: "Follow-up records",
      missingValue: "{{count}}<unit> records</unit>",
      missingNote: "Student tasks marked as missing",
    },
    method: "Only valid scores with a maximum are included. Unmarked work is not counted as zero.",
    withoutMaximum: " {{count}} scores have no maximum. Add it in Record entry.",
    assignments: {
      title: "Task performance",
      progressTitle: "Task marking progress",
      progressRegion: "Marking progress by task",
      progressValue: "{{scored}} / {{possible}} marked",
      count: "{{count}} tasks",
      count_one: "{{count}} task",
      count_other: "{{count}} tasks",
      description: "Sorted by date · Average score",
      region: "Task average scores",
      empty: "No tasks yet. Add homework, tests or dictation to see them here.",
      scored: "{{date}} · {{count}} marked",
      scoredWithoutMaximum: "{{date}} · {{count}} marked · No maximum",
    },
    distribution: {
      title: "Student average distribution",
      description: "Grouped by each student's average valid score",
      assessed: "{{count}} students",
      assessed_one: "{{count}} student",
      assessed_other: "{{count}} students",
      students: "{{count}} students",
      students_one: "{{count}} student",
      students_other: "{{count}} students",
      unassessed: "No comparable scores yet",
    },
    students: {
      title: "Student performance",
      recordsTitle: "Student records",
      description: "Select a student to view task scores and submission status.",
      searchLabel: "Search grade students",
      searchPlaceholder: "Search name or student number",
      student: "Student",
      average: "Average score",
      scored: "Marked",
      followup: "Follow-up records",
      empty: "No matching students.",
      view: "View grades for No. {{number}} {{name}}",
      details: "Grade details for No. {{number}} {{name}}",
      followupValue: "Missing {{missing}} · Late {{late}}",
      examFollowupValue: "Absent {{missing}} · Make-up {{late}}",
      noEntries: "No task records yet.",
      unrecorded: "Unrecorded",
      unscored: "Unmarked",
      noMaximum: "No maximum",
      score: "{{score}} / {{maximum}}",
    },
    statuses: {
      complete: "Submitted",
      missing: "Missing",
      late: "Late",
      excused: "Exempt",
    },
  },
  auth: {
    localIsolationTitle: "Confirm ownership of previous data",
    localIsolationChecking: "Opening this account's local data…",
    localIsolationExplanation:
      "This browser contains shared data from an older version. It is retained but hidden until its owner claims it. Other accounts can use a separate new workspace.",
    localIsolationCurrent: "Signed in as:",
    localIsolationOwnerNotice:
      "Only the owner of the previous data may claim it. If this is the wrong account, sign in with the owner's Google account first.",
    localIsolationEmail: "Enter the previous data owner's email to confirm",
    localIsolationClaim: "Confirm ownership and retain records",
    localIsolationNew: "Keep previous data and use a new workspace",
    localIsolationSwitch: "Use another Google account",
    localIsolationVerify:
      "Connect to verify this account's access before claiming data. Previous records remain intact.",
    localIsolationFailed: "Could not open local data. Existing data is retained. Sign in again and retry.",
    localIsolationRetry: "Reopen",
    accountSupport: "Account and support",
    supportLink: "Support and account deletion",
    signOut: "Sign out",
    signOutWaiting: "Waiting for saving…",
    signOutBusy: "Signing out…",
    signOutFailed:
      "Could not sign out. Check your connection or sign in again. Local data and unsaved input are retained.",
    signOutSaveFailed: "Some records could not be saved. Retry saving before signing out. Your input is retained.",
    signOutSaveTimeout: "Saving has not finished, so you have not been signed out. Wait and try again.",
    signOutUnsaved: "You have unsaved changes. Discard them and sign out? Saved local data is retained.",
    signedOutNotice: "Signed out. Classroom data in this browser is retained.",
    brandLabel: "About Free Trial",
    eyebrow: "FREE TRIAL",
    heroTitle: "Bring more order<br/>to teaching and class tasks.",
    backHome: "Back to home",
    localDataNotice: "Class and student data stays in this browser. Signing in does not automatically upload it.",
    introduction:
      "Your account identifies trial users and separates local data. Classes and student data stay in this browser and are not automatically uploaded when signing in.",
    benefits: {
      account: "Google sign-in only; first-time users can create a free account",
      localData: "Continue with local data when you reopen",
      backup: "Download your own encrypted backups",
    },
    teacherBeta: "Teacher Free Trial",
    accessLabel: "Free Trial access",
    accessReadOnlyTitle: "Read-only access",
    accessConnectionTitle: "Connection interrupted",
    accessConnectionFailed: "Could not connect to the service. Check your network and retry.",
    accessDataRetained: "Saved data remains available to view, export and back up. Unsaved input is retained.",
    accessGraceRemaining: "Saving remains available for about {{minutes}} minutes. Reconnect soon.",
    accessRetry: "Check again",
    accessUpdate: "Update this device",
    accessRetrying: "Checking…",
    accessSignIn: "Sign in again in a new tab (keep input here)",
    accessPrivacyNotice:
      "Sign-in and access checks send account identifiers; services receive IP addresses and times. Classroom data is not sent.",
    accessChecking: "Checking Free Trial access…",
    accessConfirmed: "Free Trial access confirmed.",
    accessExpired: "Free Trial access has expired.",
    accessRevoked: "Free Trial access has been revoked.",
    accessDenied: "The server has not granted Free Trial access. Check again or contact support.",
    accessUpdateRequired:
      "Another device uses a newer version of Class in Order. Update this device before you save or sync.",
    signin: {
      title: "Sign in to your account",
      description: "First-time users can create a free trial account with Google.",
    },
    previewNotice:
      "<preview>Preview mode</preview> Sign-in is not connected to Supabase yet. You can still explore the interface.",
    google: "Continue with Google",
    googleBusy: "Opening Google…",
    installPrompt: "Want quicker access next time?",
    localPreview: "Local preview only: skip sign-in →",
    legal:
      "By continuing, you agree to the <terms>Terms of service</terms> and acknowledge the <privacy>Privacy policy</privacy>. Before using real student data, confirm compliance with school rules and applicable data protection requirements.",
    errors: {
      unavailable: "Sign-in could not be completed. Try again later.",
      rateLimit: "Too many attempts. Try again later.",
      unconfigured: "Sign-in is not connected. Complete the Supabase configuration first.",
    },
  },
  screenSupport: {
    brandSymbol: "課",
    brandEnglish: "Class in Order",
    brandName: "課序",
    eyebrow: "DESKTOP FIRST",
    title: "Phone workspace not supported yet",
    description: "Open Demo and Free Trial on a computer or iPad. A screen at least 768px wide is recommended.",
    continuationTitle: "Continue later on a computer or iPad",
    continuationDescription:
      "Copy the address, send it to yourself or save it, then open it in your computer or iPad browser.",
    copyLink: "Copy trial link",
    copying: "Copying…",
    copied: "Link copied. Paste it into your own message or notes.",
    copyManually: "Your browser could not copy automatically. Press and hold the address below to copy it.",
    entryLink: "Trial entry link",
    continuationDataNote: "The link opens the entry point only. It does not transfer local classroom data.",
    detail:
      "This notice depends on screen and window width. Supported computers and iPads remain usable after zooming or resizing, with unsaved input retained.",
    legalLabel: "Policy documents",
    privacy: "Privacy policy and personal information collection statement",
    terms: "Terms of service",
    dataProcessing: "School data processing terms",
  },
  legalPolicy: {
    skip: "Skip to legal content",
    navigation: "Legal documents",
    privacyLink: "Privacy policy",
    termsLink: "Terms of service",
    dataProcessingLink: "Data processing terms",
    version: "Version {{version}} · Device-local Demo / Free Trial",
    missing: "To be completed before public release",
    operator: "Operator:",
    contactName: "Data protection contact:",
    contactEmail: "Email:",
    supportLink: "Support and account deletion",
    setupTitle: "Contact configuration incomplete",
    setupDescription: "Configure the operator name, data protection contact title, email and primary database region.",
    privacy: {
      title: "Privacy policy and personal information collection statement",
      summaryTitle: "Current data boundaries",
      summary:
        "This service is for teachers and schools in Taiwan, Hong Kong and Macau. Demo uses fictional data. Free Trial may be used in real classes with real student data, subject to school rules and applicable law. Classroom data stays in the current browser. Google sign-in, access checks and voluntary feedback you confirm require a connection. Local quizzes, AI question generation and paid plans are not available yet.",
      operatorTitle: "1. Operator and contact",
      operator:
        "The operator is responsible for the teacher account, access, voluntary feedback and necessary security data it processes. Schools or teachers manage classroom data locally; the operator does not automatically read or hold student records. Contact the person below about data protection or rights requests. Legal roles and responsibilities depend on each party's purposes, control, processing and applicable law.",
      accountTitle: "2. Teacher Google sign-in collection statement",
      account:
        "Free Trial currently offers Google sign-in only, including first-time account creation, without email registration. Google and Supabase Auth process account identifiers, email and names or profile images supplied by Google to create and identify teacher accounts, authenticate sign-in, check access, protect accounts and provide support. Data is transmitted through sign-in and processed electronically. The platform does not receive your Google password or request additional Gmail or Google Drive access. Required account information is needed to sign in to Free Trial. Without it, you can still use Demo without signing in.",
      localTitle: "3. Real classroom data and Demo",
      syncTitle: "Optional Google Drive sync",
      sync: "After a Free Trial user chooses to enable sync and authorizes Google Drive, Class in Order encrypts supported classes, students, classroom records, homework, attendance, seating and tool settings on the device before uploading them to the confirmed Drive application data space. This permission does not read general Drive files or Gmail. The sync control service processes account and public device identifiers, encrypted pairing messages and encrypted Google authorization credentials. It does not receive plaintext student records or secrets that decrypt classroom data. You must still protect local device data.",
      syncLimitedUse:
        "Class in Order uses information received from Google APIs in accordance with the Google API Services User Data Policy, including its Limited Use requirements. This information is used only for the sign-in and sync services you choose, not for advertising or training AI models.",
      syncRetention:
        "Sync requires your choice to enable it and a first QR pairing for additional devices. No biometric verification or user-managed sync keys are required. Keep at least one paired device and its website data. If all are lost, Google sign-in alone cannot decrypt cloud data. Revoking Google authorization stops future sync and does not automatically delete existing local or Drive data. Sync does not replace independent backups.",
      local:
        "Classes, student names, class numbers, optional gender, seats, roles, calendar events, attendance, homework, scores and classroom records you enter or import stay in this device's browser. Unless sync is separately enabled, signing in, saving and checking access do not upload that data to Supabase, Google, Cloudflare or AI services. Roster imports, report exports, backups and restores run on-device. Cross-device sync requires separate activation. Schools or teachers must arrange collection purposes, necessary data, notices, applicable authorisation or consent and retention. This statement does not replace school notices to students or parents.",
      demo: "Demo changes stay in memory for the current visit. Reloading or resetting restores fictional examples. Demo does not read or write Free Trial data or require teacher access checks.",
      feedbackTitle: "4. Voluntary feedback collection statement",
      feedback:
        "Feedback is optional, for resolving problems, improving the trial and following up by email if you choose. You may also email safe problem descriptions, personal data requests or follow-up questions; Gmail / Google processes support email. General support email is retained for up to 12 months from receipt. Minimal records of rights requests and deletions are generally retained for up to 12 months after completion; necessary legal holds record their basis and review date. Trial feedback is previewed before confirmation. Upon successful submission, Supabase stores your category, content, optional follow-up email, verified account identifier and receipt time for authorised operations and support staff. Feedback is not anonymous and does not automatically attach student data, class data, databases, activity logs, screenshots or backups. Do not include student names, identifiable descriptions or other people's personal data. Declining feedback or a follow-up email does not affect trial access, but we cannot follow up at an email you do not provide.",
      networkTitle: "5. Connections, recipients and cross-border processing",
      network:
        "Free Trial checks account access at startup, on return to the foreground and periodically during use. Requests carry only authentication details, without classroom data. Infrastructure still receives IP addresses, timestamps and browser and request details needed for normal connections, website delivery, troubleshooting and security. Only staff with a work-related need and the providers below process relevant data. Any legally required disclosure to authorities is limited to applicable legal requirements.",
      providerName: "Provider",
      providerUse: "Current use",
      googleName: "Google",
      supabaseName: "Supabase",
      cloudflareName: "Cloudflare",
      google: "Google sign-in, account identification and support email you send",
      supabase:
        "Teacher authentication, Free Trial access and successfully submitted feedback; no local classroom records received",
      cloudflare:
        "Website and app delivery, network security and necessary technical logs; no local classroom records received",
      region:
        "The public service will use a separate production Supabase project with its primary database in {{region}} for teacher accounts and successfully submitted feedback. Sign-in and feedback from Taiwan, Hong Kong and Macau involve overseas processing. Google sign-in and provider networking, support, logs or backups may involve other regions; the primary database location does not mean all data is processed only in Singapore. Overseas providers process data under their terms and privacy policies. Schools should assess applicable requirements before adoption. Local student data is not thereby stored in this database.",
      retentionTitle: "6. Retention, backups and deletion",
      retention:
        "Schools or teachers manage local classroom data retention according to teaching purposes and applicable law, deleting data appropriately when no longer needed. Clearing site data, reinstalling or changing browser or device may remove records. Signing in to the same Google account does not retrieve them. Download encrypted backups regularly and keep the password separately; a forgotten password cannot be recovered. Restoring replaces this account's current data in the browser. Keep a current backup and check the prompts first.",
      externalBackup:
        "If you place an encrypted backup in iCloud Drive, Proton Drive or another synced folder, your chosen sync app and service handle upload and retention. This product writes ciphertext only to the destination you confirm and does not automatically enable cloud sync. Word, Excel, PDF or CSV exports may be readable files and must be kept securely.",
      accountRetention:
        "Teacher account data is retained as needed to provide account services, including post-trial viewing, export and backup. Accounts are not deleted merely for non-payment or trial expiry. On a deletion request, or when the account service purpose ends, data is deleted or processing stops in accordance with applicable law. Only the minimum necessary data is retained for specific legal duties, unresolved disputes or security incidents, with regular reviews of necessity.",
      feedbackRetention:
        "Voluntary feedback and follow-up email are generally retained for 12 months from receipt and removed by daily cleanup after expiry. If a problem, complaint or security incident remains open, only data needed to complete it is retained, with reasons recorded and reviewed regularly, then deleted. General product improvement does not justify indefinite retention.",
      providerRetention:
        "Provider technical logs and backups follow actual service retention, rotation and cleanup settings. Deleting primary data does not guarantee every copy disappears immediately. Ask the data protection contact about provider cleanup relevant to your request. Legally retained data is not used for unrelated routine purposes. Confirmed deletion requests must be reapplied if backups are restored.",
      accessTitle: "7. Trial expiry, read-only access and recovery",
      access:
        "After the free trial, unpaid users cannot add or edit data but retain viewing, export and encrypted backup. Local data is not deleted for that reason. Expired or revoked access also becomes read-only. Following a network failure, saving remains available for 10 minutes after the last successful check, then becomes read-only. Editing resumes only after successful verification of write access. Voluntary account deletion ends sign-in, so arrange exports and backups beforehand.",
      securityTitle: "8. Device and account security",
      security:
        "Production websites and account services must use encrypted transport. Downloaded encrypted backups are password-protected; the browser database itself is not equivalent to an encrypted backup. Protect your device, browser and Google account, restrict access to student data and avoid shared browser profiles. Free Trial separates local workspaces by signed-in account; only that account's records appear after switching. Owners must confirm ownership of old shared data. This in-app separation cannot prevent someone with direct browser-file or developer-tool access from reading device data. Signing out does not clear saved records. The operator must also maintain its services, access controls and security measures.",
      requestsTitle: "9. Personal data rights, deletion and incidents",
      requests:
        "Subject to applicable law, you may request access, copies, supplementation or correction, cessation of processing, deletion or withdrawal of consent-based processing. Send teacher account and submitted feedback requests to the contact above. We verify identity reasonably and only as necessary, and respond within applicable legal deadlines. We do not require student rosters, full databases or backup passwords. If a request cannot be met fully, we explain the applicable grounds and available next steps. You may also contact or complain to your local data protection authority.",
      deletion:
        "Use the Support and account deletion contact, writing from your sign-in email. We verify identity and deletion scope and report the result after execution. Our general operating target is completion within 30 days of receipt, with prompt identity checks. This does not replace legal response, decision or processing deadlines; shorter legal deadlines prevail. We explain any necessary legal retention or lawful extension. Deleting the platform account does not delete your Google account or remotely clear device records, exports or backups you keep. Students and parents should address local student record requests to the teacher or school holding the data; the operator can provide operational support.",
      incidents:
        "If you suspect account or data exposure, provide the incident time and a description without student personal data. The operator investigates, contains and follows up incidents affecting its data and services and meets applicable notification duties. Schools or teachers must also handle incidents affecting classroom data or devices under their control.",
      reference: "This statement refers to the following guidance and legislation:",
      referenceLink: "Guidance on preparing personal information collection and privacy policy statements",
      taiwanReference: "Taiwan Personal Data Protection Act",
      macauReference: "Macau Personal Data Protection Act",
    },
    terms: {
      title: "Terms of service",
      scopeTitle: "1. Current service and eligibility",
      scope:
        "These terms apply to the device-local Class in Order Demo / Free Trial operated by {{operator}} for teachers and schools in Taiwan, Hong Kong and Macau. Demo provides fictional examples. Free Trial may be used in real classes with real student data where the purpose is lawful, data is limited to teaching management needs, and school rules and applicable data protection requirements are met.",
      adoption:
        "Teachers may create accounts individually, or schools may coordinate adoption. Each teacher currently uses a separate Google account and local data. Central school administration, shared accounts and shared cloud classroom data are not available. Creating an account does not mean a teacher contracts for the school or has obtained required school approval. Anyone arranging adoption on a school's behalf needs appropriate authority.",
      supportTitle: "2. Devices and available features",
      support:
        "The service is primarily designed for computer browsers with required local storage capabilities and screens at least 768px wide. Supported devices can continue using classroom tools when zoomed or resized. Narrow phones show a continuation guide; policy documents remain readable. Free Trial currently supports one device, with data stored in the browser in use. Local quizzes, AI question generation, automatic device sync and paid plans are not available yet.",
      freeTitle: "3. Free Trial and future payment arrangements",
      free: "Free Trial is currently free, requires no credit card and does not charge automatically. The trial does not promise permanent free service. Before launching paid plans, the operator will announce prices, trial end dates, features, device and data arrangements, and payment, cancellation and refund terms so you can decide whether to pay. Prices and launch dates remain unconfirmed. App updates or continued data viewing do not automatically create a paid subscription.",
      unpaid:
        "After the trial, unpaid users cannot add or edit data but retain viewing, export and encrypted backup. Accounts and local classroom data are not deleted merely for non-payment. These features still depend on the original browser retaining the data and the service being available; the same account cannot automatically retrieve records on another device. Any planned service closure requires a separate data-retrieval announcement and compliance with applicable law and non-excludable user rights.",
      dataTitle: "4. Local data and backup responsibilities",
      data: "Existing rights in entered or imported data do not transfer through service use. Students' and other data subjects' statutory rights are unaffected. Schools or teachers manage data content, necessary access, retention, device security, backups and exports. Free Trial keeps classroom data in the current browser, without a server copy held by the operator. After clearing site data or changing device or browser, sign-in does not restore records; use an encrypted backup you keep.",
      restore:
        "Restoring or importing a backup or previous-version file replaces current browser data without automatic merging. Back up first, verify the source and prompts, then confirm. Keep backup passwords separately; the operator cannot recover forgotten passwords.",
      conductTitle: "5. User responsibilities",
      conduct:
        "Before using real student data, confirm school policies, lawful teaching purposes and applicable authorisation, notice or consent. Consent is not the sole legal basis in every situation. Enter only necessary, appropriate and accurate data, correcting or deleting it as needed. Avoid identity numbers, medical or other sensitive information unrelated to teaching management. Where specially regulated data is necessary, confirm the corresponding legal requirements first. Do not include student personal data, Google passwords or backup passwords in feedback or support messages. Do not impersonate others, disrupt the service or bypass access controls.",
      connectionTitle: "6. Connectivity, read-only access and account closure",
      connection:
        "Initial Free Trial verification and startup or foreground checks need a connection. The 10-minute network grace period starts at the last successful check and is not extended by failures. Expired or revoked access becomes read-only immediately. Viewing, export and encrypted backup remain available without deleting local data. Account deletion follows the privacy policy. Voluntary deletion ends sign-in but does not clear device records; arrange exports and backups first.",
      changesTitle: "7. Updates, service responsibility and disputes",
      changes:
        "Free Trial features and compatibility are still improving. The operator takes reasonable measures to maintain the service, fix issues and protect data it processes, and announces material use or processing changes on the platform. Renewed consent is obtained separately where legally required. Report problems through voluntary feedback or the contacts below. The trial does not guarantee every school's administrative needs. Schools remain responsible for official records, and each party retains legally non-excludable responsibilities. These terms are not a blanket disclaimer.",
      law: "These terms are governed by Hong Kong law. First seek resolution through written contact; unresolved disputes are submitted to Hong Kong courts. Governing-law and court arrangements do not limit non-excludable data protection, consumer or other rights under applicable Taiwan, Hong Kong or Macau law. Where a school has a separate procurement contract with the operator, its agreed document priority applies, subject to mandatory law.",
      contactTitle: "8. Contact",
    },
    processing: {
      title: "School data processing terms",
      scopeTitle: "1. Scope of the local version",
      scope:
        "This document supplements the service terms and privacy policy for individual or school-coordinated use of the device-local Class in Order Demo / Free Trial in Taiwan, Hong Kong and Macau. Free Trial may process real classroom and student data. Each teacher currently uses an independent Google account and local data, without central school management or shared cloud data. Demo provides fictional examples only.",
      responsibilitiesTitle: "2. Classroom data, roles and responsibilities",
      responsibilities:
        "Classes, student names, class numbers, optional gender, classroom records, homework, scores, attendance, seats, roles, calendars and imports are processed on teachers' devices. Schools or teachers determine lawful teaching purposes, necessary data, notices, applicable authorisation or consent, retention and access according to their responsibilities. Teachers act within school authority and policy and handle accuracy and data subject requests. Whether student or parental consent is required depends on the purpose and applicable law; accepting these terms cannot replace it generally.",
      providerResponsibilities:
        "The operator supplies local-processing software and account services without automatically reading or holding student records or keeping a classroom-data server copy. This arrangement does not appoint the operator to store, grade or back up student data in the cloud. The operator must maintain the software and the account, feedback and security data it processes and fulfil applicable security and data protection duties. Legal roles depend on actual purposes, control and processing; the word 'local' or document labels do not remove statutory duties.",
      onlineTitle: "3. Accounts, access and voluntary feedback",
      online:
        "Google and Supabase process teacher account identification and access; Supabase stores feedback successfully confirmed and submitted. The public service will use a separate production project with its primary database in Singapore. Google sign-in and other provider services may involve other regions. Cloudflare provides the website and necessary network security. These services do not thereby receive local student data. See the privacy policy for data categories, purposes, recipients, retention and rights requests. Schools must assess overseas account and feedback processing against applicable requirements.",
      backupTitle: "4. Exports, backups and independently chosen sync services",
      backup:
        "Exports and encrypted backups are created on-device. Schools or teachers must limit device and browser-profile access, safeguard files and passwords, back up and delete under school policy, and review replacement warnings before restoring. Exported reports may be directly readable. Encrypted backups do not mean the browser database itself is encrypted. Assess permissions, locations and retention before using sync folders or cloud storage. Such use does not automatically enable device sync in this service.",
      assistanceTitle: "5. Access, correction, deletion and incident support",
      assistance:
        "Schools or teachers holding local classroom data handle student or parent access, correction and deletion requests under applicable law. The operator can provide operational support but cannot remotely read, recover or delete local records. Teacher account and submitted feedback requests go to the operator under the privacy policy and statutory deadlines. Each party takes appropriate measures for incidents affecting data and services it controls, including legal notifications. Support messages should describe problems without student personal data, full databases or backup passwords.",
      endingTitle: "6. Read-only access, leaving and data handover",
      ending:
        "After the free trial, unpaid users cannot add or edit but retain viewing, export and encrypted backup. Non-payment alone does not delete accounts or local classroom data. Access expiry, revocation or the end of network grace also makes the app read-only. When a teacher leaves, transfers school or stops using the service, schools and teachers must arrange lawful handover, retrieval and deletion under school policy. This does not entitle departing teachers to keep student records indefinitely. Signing out or deleting an account does not clear local data. Export and back up before voluntary account deletion.",
      futureTitle: "7. Future paid services and additional processing",
      future:
        "Payment does not automatically authorise uploading existing local student data. Before introducing central school administration, sharing, cloud storage, automatic sync, student quizzes or AI, the service must separately explain processing scope, legal roles, providers, regions, security, retention and deletion, and obtain required agreements, authorisation or consent. Current local-trial terms do not pre-authorise that processing.",
      contactTitle: "8. Contact",
    },
  },
  support: {
    skip: "Skip to support content",
    navigation: "Support navigation",
    privacy: "Privacy policy",
    terms: "Terms of service",
    app: "Back to Free Trial",
    title: "Support and account deletion",
    intro:
      "Class in Order is supported by {{operator}}. Contact the data protection officer below about general problems, personal data requests or security incidents.",
    helpTitle: "Help and follow-up",
    help: "Signed-in teachers can preview and submit Trial feedback. If you cannot sign in or need an email response, write directly.",
    safeDetails:
      "Include the time, device and browser versions, steps and error text with personal data removed. Do not send student rosters, classroom databases, full backups, Google passwords or backup passwords. Hide student and other personal data in screenshots.",
    response:
      "Our target is to acknowledge and classify general enquiries within three Hong Kong working days. Resolution time depends on the issue; we explain follow-up when more time is needed. This is a service target, not immediate or round-the-clock support.",
    helpSubject: "Class in Order | Support",
    helpAction: "Email a support question",
    deleteTitle: "Request platform account deletion",
    deleteBefore:
      "After deletion, you cannot sign in with the original Class in Order account. Export required records and download an encrypted backup in Data and backups first. Check and safeguard the files.",
    deleteRequest:
      "Write from your sign-in email with the subject 'Class in Order | Delete account', requesting deletion of your Class in Order account and submitted feedback. Do not attach student data.",
    deleteVerify:
      "We verify the request through the original account email, confirm export arrangements and deletion scope, and do not delete before verification is complete.",
    deleteComplete:
      "After confirmation, we remove the platform sign-in account and related feedback, verify the result, and email completion details or the minimum legally retained data and reasons.",
    deleteScope:
      "This deletes your Class in Order account, not your Google account. Signing in with Google later may create a new Class in Order account.",
    deleteLocal:
      "The platform cannot remotely clear device records, exports or backups. Follow school rules for handover and local deletion after stopping use. Signing out or switching Google accounts does not clear local data.",
    deadline:
      "The general target is completion within 30 days of receipt. Shorter legal deadlines prevail. Identity checks do not arbitrarily reset the starting date. We explain lawful retention or extensions.",
    deleteSubject: "Class in Order | Delete account",
    deleteAction: "Email an account deletion request",
    emailNotice:
      "This link only opens your email app. Review and send the message yourself; clicking does not submit a request or delete an account.",
    rightsTitle: "Access, correction and other personal data requests",
    rights:
      "Describe the scope and your jurisdiction. We handle requests within applicable deadlines. Students or parents should contact the teacher or school holding local records; we can provide operational support. An identity-card copy is not required by default.",
    rightsSubject: "Class in Order | Personal data request",
    rightsAction: "Contact the data protection officer",
    feedbackTitle: "Feedback retention and deletion",
    feedback:
      "Trial feedback is not anonymous. It contains only the category, content, optional follow-up email, account identifier and receipt time you confirm, without automatic student data or screenshots.",
    feedbackRetention:
      "Feedback is generally retained for 12 months and deleted in daily cleanup after expiry. For open complaints, security incidents or legal duties, only necessary data is kept, with a reason and review date, and deleted on completion. You may request earlier deletion.",
    securityTitle: "Account or data security incidents",
    security:
      "Use 'Security incident' in the subject and provide the time, affected data categories and a safe description. The operator prioritises assessment, containment and follow-up of its service incidents. Also inform your school immediately about device or local student-data incidents.",
    securitySubject: "Class in Order | Security incident",
    securityAction: "Report a security incident",
  },
  pwaInstall: {
    checkUpdate: "Check for updates",
    updatePending: "Update needs attention",
    updateStatus: "Class in Order updates",
    closeStatus: "Collapse",
    versionLabel: "Version {{build}}",
    currentVersion: "Current version: {{build}}",
    versionChanged: "A newer version is available. Check again and wait for the download to finish.",
    websiteSignIn: "Sign in to the website",
    accessRestored: "Connected to Class in Order",
    returnToUpdate:
      "Return to your original Class in Order tab to check for updates automatically. Unsaved work remains in that tab.",
    returnToWorkspace: "Return to the original tab",
    status: {
      idle: "Select Check for updates to compare with the website.",
      checking: "Checking the website version…",
      current: "You are using the latest version.",
      downloading: "Downloading a new version. You can continue working.",
      ready: "The update is downloaded. Save your work before updating.",
      "auth-required":
        "Sign in to the website to check or download updates. Returning here checks again automatically.",
      offline: "You are offline. Updates cannot be checked. Saved local data is retained.",
      failed:
        "The update check or download could not finish. Stay connected and try again. Your current version and local data are retained.",
    },
    updateTitle: "A Free Trial update is available",
    installed: "✓ Added to home screen",
    install: "Add to home screen for quicker access",
    iosHelp: "iPhone / iPad: in Safari, select Share → Add to Home Screen.",
    browserHelp: "Use your browser menu to choose Install app / Add to Home Screen.",
  },
  guidedTour: {
    regions: {
      demo: "Demo feature tour",
      setup: "Free Trial interactive setup",
    },
    mascot: "課",
    progress: {
      demo: "Demo feature tour · {{chapter}} / {{total}}",
      setup: "Setup guide · Task {{chapter}} / {{total}}",
    },
    preparing: "Preparing the highlighted area. You can skip this step and reopen the guide later.",
    actions: {
      close: "Close tour",
      skip: "Skip tutorial",
      manualSetup: "Set up myself",
      editRoster: "← Back to edit roster",
      back: "← Previous",
    },
    steps: {
      className: {
        title: "Name your class first",
        message: "Enter a test class in the highlighted field, such as '1A Chinese', then select Next.",
        next: "Next →",
      },
      classRole: {
        title: "Choose the class purpose",
        message:
          "Teaching classes record lessons. Homeroom classes offer attendance and class management tools. Choose the appropriate purpose to continue.",
        next: "Next →",
      },
      classSubmit: {
        title: "Create your first class",
        message: "Select the highlighted Add class button. Once created, we will continue to roster import.",
      },
      rosterLink: {
        title: "Next: student roster",
        message: "Select Import roster to open this class's import field.",
      },
      rosterInput: {
        title: "Paste your student roster",
        message:
          "Copy the roster from Excel and paste it here. Column headings are optional. Imported data stays in the current browser.",
        next: "Ready, next →",
      },
      rosterSubmit: {
        title: "Check and import",
        message:
          "Select Check and import. Correct any roster problems and retry; successful imports continue to the seating chart.",
      },
      seatingLink: {
        title: "Arrange your seating chart",
        message: "Select Arrange seats to open this class's seating chart.",
      },
      seatingNav: {
        title: "Open the seating chart",
        message: "Select Seating chart in the left sidebar. If the sidebar is hidden, use the button below.",
        next: "Take me to seating →",
      },
      seatingStudent: {
        title: "Choose a student first",
        message:
          "Select the highlighted student. Dragging is also available, but selecting is easier for this first step.",
      },
      seatingSeat: {
        title: "Then select an empty seat",
        message: "After choosing a student, select the highlighted empty seat to place them.",
      },
      seatingSave: {
        title: "Saving seating chart",
        message: "Seating arrangements save automatically. Once saved, continue arranging the remaining students.",
      },
      demoWelcome: {
        title: "Explore each feature",
        message:
          "Demo includes fictional classes, rosters and records. Each page introduces a feature. Go back or close the tour at any time.",
        next: "Start feature tour →",
      },
      demoToday: {
        title: "Calendar home",
        message:
          "View events across classes and the school for the next two weeks or the full year. Select the Class in Order logo at the top left to return home.",
        next: "Next feature →",
      },
      demoPerformance: {
        title: "Class participation",
        message:
          "Review responses, missing books and notes, and comments by student. Filter records needing attention, or select Compare terms to see changes across terms.",
        next: "Next feature →",
      },
      demoCoursework: {
        title: "Coursework and grades",
        message:
          "Coursework overview highlights follow-up items. Grade table shows class results, while Record entry handles scores and submission status.",
        next: "Next feature →",
      },
      demoGroups: {
        title: "Picker and groups",
        message: "Randomly select students from the class roster or create groups for class questions and activities.",
        next: "Next feature →",
      },
      demoSeating: {
        title: "Seating chart",
        message:
          "Arrange seats and switch teacher or student views. Attendance and classroom records reuse the same chart.",
        next: "Next feature →",
      },
      demoCalendar: {
        title: "Calendar",
        message:
          "View school and class events. Select '+ New event' for homework, tests, dictation or reminders. Coursework dates link to student records in Coursework and grades.",
        next: "Next feature →",
      },
      demoAttendance: {
        title: "Homeroom attendance",
        message: "Select seats to toggle present or absent. Each change automatically saves the day's attendance.",
        next: "Next feature →",
      },
      demoHomeroomHomework: {
        title: "Missing homework",
        message: "Record missing work by subject and task so homeroom teachers can follow up late submissions.",
        next: "Next feature →",
      },
      demoStudentRecords: {
        title: "Individual student records",
        message:
          "Review each student's attendance, missing work and daily follow-up records together, and export when needed.",
        next: "Next feature →",
      },
      demoHomeroom: {
        title: "Class affairs",
        message:
          "Daily memos keep ongoing student arrangements. The class log records meetings, notices and follow-up matters.",
        next: "Next feature →",
      },
      demoHomeroomDuties: {
        title: "Student roles",
        message: "Use the roster to assign monitors, subject representatives and cleaning duties in one place.",
        next: "Next feature →",
      },
      demoToolSettings: {
        title: "Tool settings",
        message:
          "In Administration → Tool settings, choose which sidebar tools to show. Hidden tools retain their data.",
        next: "Next feature →",
      },
      demoRoster: {
        title: "Administration",
        message: "Create classes, import rosters, manage the school calendar and add historical records.",
        next: "Next feature →",
      },
      demoData: {
        title: "Plan and data",
        message:
          "Demo changes last for this visit and reset on reload. Free Trial saves your own classroom data on the same device.",
        next: "Finish tour →",
      },
    },
  },
  homeroomNotebook: {
    title: "Class affairs log",
    addTitle: "Record class affairs",
    date: "Date",
    event: "Event",
    note: "Details / follow-up",
    add: "Add entry",
    edit: "Edit",
    editLabel: "Edit entry: {{title}}",
    editTitle: "Edit class affairs entry",
    save: "Save changes",
    saving: "Saving…",
    cancel: "Cancel",
    delete: "Delete entry",
    deleteTitle: "Delete this entry?",
    deleteMessage: "“{{title}}” will be deleted. Other entries will remain.",
    confirmDelete: "Confirm deletion",
    back: "Back to editing",
    failed: "Could not save. Your input is retained; please retry.",
    stale: "This entry was updated elsewhere. Cancel and reopen it to review the latest content.",
    discard: "Discard unsaved changes?",
    empty: "No class affairs entries yet.",
    added: "Entry added",
    updated: "Entry updated",
    deleted: "Entry deleted",
    search: "Search class affairs",
    placeholder: "For example: parents’ day arrangements or class meeting follow-up",
  },
  assignmentRecords: {
    batch: {
      open: "Batch entry",
      title: "Batch entry",
      statusTab: "Class status",
      scoresTab: "Paste scores",
      scope: "Apply to",
      unrecorded: "Unrecorded students ({{count}})",
      everyone: "Whole class ({{count}})",
      status: "Set status",
      applyStatus: "Apply to {{count}} students",
      replaceStatus: "Updates the selected students’ status and keeps existing scores.",
      format: "Paste format",
      order: "Roster order: one score column",
      number: "Class number and score: two columns",
      text: "Paste cells from Excel",
      orderHint: "One row per student in roster order. Blank scores are skipped.",
      numberHint:
        "Class number in column 1, score in column 2. For mixed classes, use the full number, such as 4A (12). Blank scores are skipped.",
      preview: "Review scores",
      student: "Student",
      previous: "Current score",
      next: "New score",
      skipped: "Skip",
      applyScores: "Apply {{count}} scores",
      replaceScores: "Updates {{count}} scores, including {{existing}} existing scores.",
      errorLine: "Row {{line}}: {{error}}",
      errors: {
        rowCount: "The row count must match the class size. Keep rows for blank scores.",
        columns: "The column count does not match the selected format. Paste Excel cells.",
        student: "The class number does not identify one student. Check the full number.",
        duplicate: "This student appears more than once.",
        score: "Enter a valid non-negative score within the maximum mark.",
      },
      cancel: "Cancel",
      close: "Close batch entry",
      discard: "Close without applying batch input?",
      view: "Score entry view",
      table: "Table",
      cards: "Cards",
      tableHint: "Enter: next student · Shift + Enter: previous",
      tableLabel: "Class score entry table",
      statusApplied: "Applied status to {{count}} students",
      scoresApplied: "Applied {{count}} scores",
    },
    copy: {
      open: "Copy to other classes",
      title: "Copy coursework to other classes",
      description: "Copies the task details, maximum mark and notes. Each class records its own status and scores.",
      class: "Teaching class",
      date: "Date",
      folder: "Folder",
      select: "Select {{name}}",
      dateLabel: "{{name}}: date",
      folderLabel: "{{name}}: folder",
      empty: "No other teaching classes are available in this school year.",
      apply: "Copy to {{count}} classes",
      saving: "Copying…",
      cancel: "Cancel",
      failed: "Could not complete the copy. Check classes, dates and folders, then retry.",
      stale: "The source task has changed. Close and reopen this dialog to review the latest details.",
      success: "Copied to {{count}} classes",
      discard: "Discard the unfinished copy settings?",
    },
    studentRecords: "Student coursework",
    invalidScore: "Scores must be zero or higher and must not exceed the maximum. Leave blank to clear a score.",
    scoreRejected: "Enter zero or higher. The previous value is retained.",
    scoreRangeRejected: "Enter a score from 0 to {{maxScore}}. The previous value is retained.",
    enterRecords: "Scores / submissions",
    enterExamRecords: "Scores / attendance",
    enterExamRecordsLabel: "Scores / attendance: {{title}}",
    actions: "Actions",
    enterRecordsLabel: "Scores / submissions: {{title}}",
    moreActions: "More task actions: {{title}}",
    editMetadata: "Edit task details",
    createTitle: "New task record",
    createAndRecord: "Create and enter scores",
    saveMetadata: "Save task details",
    cancel: "Cancel",
    close: "Close task form",
    discardConfirm: "You have unsaved task details. Discard changes?",
    saveRecords: "Save student records",
    delete: "Delete task and scores",
    deleteTitle: "Confirm task and score deletion",
    deleteMessage: "Delete '{{title}}' and saved records for {{count}} students?",
    deleteMessage_one: "Delete '{{title}}' and saved records for {{count}} student?",
    deleteMessage_other: "Delete '{{title}}' and saved records for {{count}} students?",
    deleteWarning:
      "Linked calendar events are removed too, except shared events. This cannot be undone. Export or download an encrypted backup first if you need to retain the data.",
    confirmDelete: "Delete task and all scores",
    deleteFailed: "Could not delete. The task and scores are retained. Try again.",
    deleteUnavailable: "Task deletion is unavailable in this version. Data is retained.",
    deleteChanged: "The task or student records have changed. Review and confirm deletion again.",
    directoryTitle: "Record directory",
    folders: {
      create: "New folder",
      manageTitle: "Manage folder",
      manage: "Manage '{{name}}' folder",
      open: "Open {{name}} category",
      name: "Folder name",
      folder: "Folder",
      recordType: "Task category",
      count: "{{count}} records",
      count_one: "{{count}} record",
      count_other: "{{count}} records",
      uncategorized: "Unfiled",
      saveName: "Save name",
      delete: "Delete folder",
      deleteTitle: "Confirm folder deletion",
      deleteMessage:
        "Delete '{{name}}'? Its {{count}} records across all terms move to Unfiled. Student status and scores are retained.",
      deleteMessage_one:
        "Delete '{{name}}'? Its {{count}} record across all terms move to Unfiled. Student status and scores are retained.",
      deleteMessage_other:
        "Delete '{{name}}'? Its {{count}} records across all terms move to Unfiled. Student status and scores are retained.",
      confirmDelete: "Delete and keep records",
      cancel: "Cancel",
      saving: "Saving…",
      failed: "Could not save. Use a unique folder name or reopen and try again.",
      defaultNames: {
        homework: "Homework",
        assessment: "Tests",
        dictation: "Dictation",
      },
      date: "Task date",
      record: "Student records",
    },
    emptyCategory: "No task records in this category yet.",
    schoolYear: "View school year",
    schoolYearLabel: "{{year}} school year",
    viewTerm: "View term",
    unassignedTerm: "No term configured",
    assessmentDate: "Test date",
    dictationDate: "Dictation date",
    homework: {
      statuses: {
        complete: "On time",
        missing: "Missing",
        late: "Submitted late",
        excused: "Exempt",
      },
      pendingMissing: "Awaiting submission",
      date: "Due date",
      record: "Submission records",
      state: "Submission status",
      studentState: "No. {{number}} {{name}}: submission status",
      missingList: "Still missing: {{students}}",
      noMissing: "No students marked as missing",
      saved: "Student records saved. Return to the directory to see missing submissions.",
    },
    exam: {
      statuses: {
        complete: "Attended",
        missing: "Absent",
        late: "Make-up completed",
        excused: "Exempt",
      },
      record: "Assessment records",
      state: "Assessment attendance",
      studentState: "No. {{number}} {{name}}: assessment attendance",
      missingList: "Absent: {{students}}",
      noMissing: "No students marked as absent",
      saved: "Student assessment records saved.",
    },
  },
  recordSaveBar: {
    ready: "No records entered yet",
    restored: "Unsaved changes reset",
    group: "{{label}} actions",
    changed: "{{count}} unsaved changes",
    saved: "Saved",
    saving: "Saving…",
    restore: "Reset changes",
    restoreConfirm: "Restore the last saved content? This discards unsaved changes on screen.",
  },
  localData: {
    storageWarning: "Clearing site data may remove records. Download a backup first.",
    persistenceRequest: "Request persistent storage",
    persistenceResult: "Persistent storage request result",
    persistenceChecking: "Checking",
    persistenceExplanation: "The browser may approve or decline automatically without showing a permission prompt.",
    persistencePending: "Requesting persistent storage from the browser. Please wait…",
    persistenceGranted: "Persistent storage granted. Continue downloading backups regularly.",
    persistenceNotGranted:
      "Persistent storage was not granted. Data can still be saved locally. Download backups regularly and try again later.",
    persistenceUnsupported:
      "This browser does not support persistent storage requests. Data can still be saved locally. Download backups regularly.",
    persistenceError: "Could not complete the storage request. Try again later and keep regular backups.",
    passwordWarning: "You cannot restore a backup if you forget its password.",
    restoreWarning: "Restoring replaces all local classroom data. Download a current backup first.",
  },
  rosterPaste: {
    crossClass: "Cross-class elective roster",
    sourceClass: "Original class",
    crossClassMissing: "A cross-class roster needs Class, Class No. and Name columns, with a class for every student.",

    importTitle: "Import students",
    format: "Import format",
    excelMethod: "Excel file",
    csvMethod: "CSV",
    csv: {
      file: "Student roster CSV (.csv, up to 10 MB)",
      content: "CSV content",
      placeholder: "Number,Name,Gender\n1,Student A,M\n2,Taylor Chan,F",
      cancel: "Cancel CSV import",
      reading: "Reading CSV…",
      errors: {
        fileType: "Choose a .csv file.",
        size: "Maximum CSV file size is 10 MB. Split the roster first.",
        read: "Could not read CSV. Save it as UTF-8 CSV and try again.",
        empty: "The CSV file contains no students.",
        cell: "Could not read the CSV content.",
      },
    },
    excel: {
      title: "Import Excel file",
      settings: "Excel roster import settings",
      file: "Student roster Excel (.xlsx, up to 10 MB)",
      reading: "Reading Excel…",
      sheet: "Worksheet",
      genders: { M: "Male", F: "Female", X: "Other" },
      preview: "Students to import",
      summary:
        "{{className}} · {{count}} students ({{updates}} will update students with the same original class and number)",
      cancel: "Cancel Excel import",
      confirm: "Confirm import of {{count}} students",
      saving: "Importing…",
      errors: {
        fileType: "Choose an .xlsx file. Save older .xls files as .xlsx in Excel first.",
        columns:
          "Could not identify the student columns. Use Number and Name headings, with an optional Gender column, then select the file again.",
        class: "No class in the roster matches {{className}}. Check the class name or choose another roster.",
        size: "Maximum 10 MB, 5,002 rows and 80 columns per sheet, and 50,000 populated cells per file. Split the roster first.",
        empty: "The Excel file has no readable data.",
        read: "Could not read Excel. Check that it is not damaged or encrypted, then save as .xlsx and try again.",
        cell: "Some cells have no readable value. Recalculate formulas in Excel, or paste the roster as values before importing.",
      },
    },
    input: "Student roster text",
    placeholder: "1\tStudent A\tm\n2\tTaylor Chan\tf",
    title: "Paste Excel roster",
    submit: "Check and import",
    errors: {
      empty: "The roster contains no students.",
      class: "The file includes class data. Choose the source class to import.",
      format: "Row {{line}} is incomplete. Check class numbers (1–999), names and the number of columns in each row.",
      columns:
        "Could not identify the roster. Copy only class number, name and optional gender cells and paste again. Columns may be in any order.",
      gender:
        "Gender in row {{line}} is unrecognised. Use Male / Female / Other or M / F / X (case-insensitive), or leave it blank.",
      duplicate: "Duplicate class number {{number}}. Check the roster.",
    },
  },
  classroomInterface: {
    missingWorkAutosaveHint:
      "Task details are optional. Select a subject and missing students to autosave. Reply slips do not need a subject.",
    recordLocalOnly: "This recording feature is available only in the local version and Demo.",
    demoLabel: "Demo · Fictional data",
    resetDemo: "Reset demo data",
    demoSchoolYear: "Demo · Full-year fictional data · {{year}} · 3 terms",
    localTrial: "Free Trial · On this device",
    continueSetupTitle: "Finish class setup",
    setupTutorialComplete: "Basic tutorial complete",
    rosterAndSeating: "Student roster and seating chart",
    backupReminder: "Persistent storage has not been granted. Back up regularly.",
    backupReminderShort: "Back up regularly",
    rosterFormat: "Paste a roster from Excel with Chinese or English names. Headings and gender are optional.",
    autoSave: "Autosave",
    noStudents: "No student roster yet.",
    noRoutineItems: "No daily tasks created yet.",
    chooseSubject: "Choose a subject.",
    sharedSeating: "Shared seating chart",
    noAnswers: "No answers received yet.",
    demoDataNotice: "Demo uses fictional data and resets on reload.",
    demoTrialAction: "Want to save your own classroom data? Open Free Trial →",
    homeTitle: "Calendar home",
    switchClass: "Switch class",
    tools: "Tools",
    openTools: "Open tools",
    closeTools: "Close tools",
    primaryTools: "Main tools",
    sidebarHidden: "Collapse sidebar",
    sidebarExpand: "Expand sidebar",
    print: "Print",
    export: "Export",
    exportIcs: "Export ICS",
    exportSeatingHint: "Export this class's seating chart as PDF",
    exportRecordsHint: "Export this class's student records",
    exportContextHint: "Export saved records for this class and selected scope",
    required: "Enter an event name and date.",
    endBeforeStart: "End date cannot be before start date.",
    saved: "'{{title}}' added to calendar",
    saveFailed: "Could not add the event. Try again.",
    entryTitle: "New classroom event",
    closeEntry: "Close new classroom event",
    entryHelp: "Add to the '{{className}}' calendar. It appears as soon as it is saved.",
    itemType: "Event type",
    other: "Other",
    itemName: "Event name",
    titlePlaceholder: "e.g. Complete worksheet 3",
    date: "Date",
    note: "Additional notes (optional)",
    notePlaceholder: "e.g. Topics, page numbers or materials to bring",
    moreSettings: "More calendar settings",
    schoolType: "School calendar category",
    endDate: "End date (optional)",
    cancel: "Cancel",
    saving: "Adding…",
    save: "Add to calendar",
    help: "Help",
    back: "← Back",
    replayDemo: "↻ Replay feature tour",
    replayLocal: "↻ Restart setup guide",
    classSelect: "Choose class",
    classMenu: "Class menu",
    classTeaching: "Teaching classes",
    classHomeroom: "Homeroom class",
    studentCountLabel: "Class student count",
    dayCount: "View {{count}} events on {{date}}",
    dayCount_one: "View {{count}} event on {{date}}",
    dayCount_other: "View {{count}} events on {{date}}",
    moreEvents: "＋{{count}} more",
    countUnit: " events",
    dayTitle: "Events on {{date}}",
    holidayBadge: "Day off",
    holidayDetail: "View holiday on {{date}}: {{title}}",
    eventCount: "{{count}} events",
    eventCount_one: "{{count}} event",
    eventCount_other: "{{count}} events",
    shortEventCount: "{{count}} events",
    shortEventCount_one: "{{count}} event",
    shortEventCount_other: "{{count}} events",
    holidayCount: "{{count}} holidays",
    holidayCount_one: "{{count}} holiday",
    holidayCount_other: "{{count}} holidays",
  },
  localExport: {
    print: "Print",
    printTarget: "Print {{target}}",
    printPreview: "Print table preview",
    printFrom: "Print start date",
    printTo: "Print end date",
    printFailed: "Could not print. Try again.",
    printOpened: "Print opened.",
    printBoard: "Board",
    printTeacher: "Teacher view",
    printStudent: "Student view",
    printEmptySeat: "Empty seat",
    printNoSeating: "No seating chart saved yet",
    exportTarget: "Export {{target}}",
    recordTypes: {
      records: "Student records",
      homework: "Homework tasks",
      attendance: "Attendance records",
      homeroomHomework: "Missing homework",
      studentRecords: "Student records",
      homeroom: "Class affairs",
      homeroomDuties: "Student roles",
      seating: "Seating chart",
      calendar: "Calendar events",
      quiz: "Quiz records",
      roster: "Student roster",
    },
    close: "Close",
    allDates: "All dates",
    privacy: "Conversion and export run on this device without uploading to a conversion service.",
    savedOnly: "Only saved records are exported. Save your changes first.",
    filteredScope: "Uses this page's filters. For other classes or record types, use Administration → Export records.",
    rangeTitle: "Date range",
    dateSeparator: "to",
    from: "Export start date",
    to: "Export end date",
    rangeHint:
      "Both dates are included. Leave dates blank to use the current term, or choose All dates to export every date.",
    undated: "Rosters, current roles and seating charts are not filtered by date.",
    sheet: "Preview table",
    preview: "Export table preview",
    rowCount: "{{count}} records",
    rowCount_one: "{{count}} record",
    rowCount_other: "{{count}} records",
    previewLimit: "Preview shows up to 10 records per page. Files and copied content include all filtered records.",
    csvHint:
      "CSV and copying use the selected table above. Excel, PDF and JSON export the whole report. CSV uses UTF-8 and YYYY-MM-DD dates. For numbers with leading zeros, import the column as text or use Excel. Blank scores stay blank. JSON record exports cannot restore a full backup.",
    formats: {
      xlsx: "Excel",
      csv: "CSV",
      pdf: "PDF",
      json: "JSON",
    },
    csv: "Download CSV",
    pdfPreview: "PDF page preview",
    pdfPage: "Page {{page}} of {{count}}",
    pdfLoading: "Loading PDF preview…",
    pdfPreviewFailed: "Could not load the PDF preview. Please try again.",
    downloadPdf: "Download PDF",
    pdfDownloadStarted: "PDF download started.",
    retryPdf: "Reload preview",
    backToExport: "Back to export options",
    copy: "Copy table",
    copied: "Table copied.",
    downloaded: "Export downloaded.",
    working: "Generating…",
    failed: "Could not export. Try again.",
    copyFailed: "Could not access the clipboard. Download CSV instead.",
    errors: {
      invalidDate: "Enter valid dates.",
      order: "End date cannot be before start date.",
      outsideTerm: "Export dates must fall within the current term. Choose All dates first for other dates.",
    },
  },
  publicHolidays: {
    title: "Hong Kong public holidays",
    coverage: "Automatically marked for {{start}}–{{end}}",
    source: "Official source: 1823",
    unavailable: "Official holiday data for {{years}} is not available yet.",
    importPurpose:
      "School calendar imports review school activities and holidays. Matching public holidays are already marked and need not be imported again.",
    alreadyMarked: "Official public holiday already marked; no need to import again",
    officialDetail: "Hong Kong Government public holiday · Automatically marked",
  },
  calendarImport: {
    excelReview: "Excel calendar source and column mapping",
    sheet: "Worksheet",
    missingMapping: "Date and event columns not recognised: map columns manually",
    changeMapping: "View or edit column mapping",
    headerRow: "Header row number (0 for no header)",
    startColumn: "Start date column",
    titleColumn: "Event title column",
    endColumn: "End date column (optional)",
    noteColumn: "Note column (optional)",
    chooseColumn: "Choose column",
    none: "None",
    row: "Row number",
    sourcePreview: "Original worksheet preview (first 5 rows)",
    applyMapping: "Apply columns and analyse again",
    reparseHint:
      "Analysing again resets review edits and selections. Column settings for other worksheets are retained.",
    unrecognised: "Unmatched source text ({{count}} rows)",
    unrecognised_one: "Unmatched source text ({{count}} row)",
    unrecognised_other: "Unmatched source text ({{count}} rows)",
    unrecognisedHint:
      "Includes unrecognised content, headings and date cells. Switch worksheets to review. Add needed events to the review and fill in dates and names.",
    sheetUnrecognised: "{{name}}: {{count}} rows",
    sheetUnrecognised_one: "{{name}}: {{count}} row",
    sheetUnrecognised_other: "{{name}}: {{count}} rows",
    addToReview: "Add to review",
    addedToReview: "Added to review",
    previous: "Previous",
    next: "Next",
    page: "Page {{current}} / {{total}}",
    reviewBeforeImport: "Not imported yet. Review events and unmatched source text, then confirm import.",
    duplicateInFile: "An event with this date and name already exists in this file",
    invalidYear: "Enter a school year starting year from 2000 to 2100.",
    invalidHeader: "Header row number must be an integer from 0 to 10,000.",
  },
} satisfies Messages<typeof zhHK>;
